import React, { useState, useEffect, useRef, Suspense, useCallback  } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import GridViewIcon from '@mui/icons-material/GridView';
import appService from '../../../services/ApplicationService';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import ModernSelect from '../../common/ModernSelect';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import CopyAllRoundedIcon from '@mui/icons-material/CopyAllRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Modal from '../../common/Modal';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccessProvisioningTab from './tabs/AccessProvisioningTab';
import UserTerminationTab from './tabs/UserTerminationTab';
import UserAccessReviewTab from './tabs/UserAccessReviewTab';
import PrivilegedAccountsTab from './tabs/PrivilegedAccountsTab';
import ChangeManagementTab from './tabs/ChangeManagementTab';
import styled from '@emotion/styled';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { alpha } from '@mui/material/styles';
// Add questionnaire UI imports
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import QuizIcon from '@mui/icons-material/Quiz';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SystemUpdateAlt from '@mui/icons-material/SystemUpdateAlt';
import Settings from '@mui/icons-material/Settings';

// Add questionnaire UI components
const QuestionnaireCard = styled(mui.Paper)(({ theme }) => ({
  padding: '16px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: '1px solid',
  borderColor: alpha('#e0e0e0', 0.5),
  background: '#fff',
}));

const QuestionTitle = styled(mui.Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '1.25rem',
  color: '#3a3a3a',
  '& .MuiSvgIcon-root': {
    marginRight: '8px',
    color: '#1976d2',
  },
}));

const QuestionDescription = styled(mui.Typography)(({ theme }) => ({
  marginBottom: '32px',
  color: '#707070',
}));

const NavigationButtons = styled(mui.Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: '1px solid',
  borderColor: alpha('#e0e0e0', 0.5),
}));

// Modern minimalist styled components
const FloatingAction = mui.styled(mui.Zoom)(({ theme }) => ({
  position: 'fixed',
  bottom: '32px',
  right: '32px',
  zIndex: 1000,
}));

// Saving indicator
const SavingIndicator = mui.styled(mui.Fade)(({ theme }) => ({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  zIndex: 9999,
}));

// Define scrollToTop function that will be used by multiple components
const scrollToTop = () => {
    // Use setTimeout to ensure this runs after React has updated the DOM
    setTimeout(() => {
        // Try multiple scroll methods for better compatibility
        try {
            // Try to scroll the content container first (most specific target)
            const contentContainer = document.getElementById('application-controls-content');
            if (contentContainer) {
                contentContainer.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                console.log('Content container scrolled to top');
            }
            
            // Also try window scrolling
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            
            // As a fallback (for older browsers), also try:
            document.body.scrollTop = 0; // For Safari
            document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
            
            console.log('Scroll to top executed');
        } catch (error) {
            console.error('Error scrolling to top:', error);
            // Basic fallback
            window.scrollTo(0, 0);
        }
    }, 100); // Short delay to ensure DOM updates have completed
};

// Add this new component definition before the MyApplicationControls component
const CompletionStatus = ({ completedCount, totalCount }) => {
    const percentage = Math.round((completedCount / totalCount) * 100);
    
    return (
        <mui.Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: alpha('#f5f5f5', 0.7),
            border: `1px solid ${alpha('#e0e0e0', 0.5)}`
        }}>
            <mui.Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <mui.Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                    Completion: 
                </mui.Typography>
                <mui.Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                    {completedCount}/{totalCount}
                </mui.Typography>
                <mui.Box sx={{ 
                    display: 'flex',
                    alignItems: 'center', 
                    backgroundColor: percentage === 100 ? alpha('#4caf50', 0.1) : alpha('#1976d2', 0.1),
                    borderRadius: '12px',
                    padding: '1px 6px',
                    ml: 1
                }}>
                    {percentage === 100 ? (
                        <CheckCircleRoundedIcon sx={{ fontSize: '12px', color: 'success.main', mr: 0.5 }} />
                    ) : (
                        <AssignmentLateIcon sx={{ fontSize: '12px', color: 'primary.main', mr: 0.5 }} />
                    )}
                    <mui.Typography variant="caption" sx={{ 
                        color: percentage === 100 ? 'success.main' : 'primary.main',
                        fontWeight: 500
                    }}>
                        {percentage}%
                    </mui.Typography>
                </mui.Box>
            </mui.Box>
            <mui.Box sx={{ width: '80px' }}>
                <mui.LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                        height: 5,
                        borderRadius: 2,
                        backgroundColor: alpha('#e0e0e0', 0.5),
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            backgroundColor: percentage === 100 ? 'success.main' : 'primary.main',
                        }
                    }}
                />
            </mui.Box>
        </mui.Box>
    );
};

// Step progress bar component
const StepProgressBar = ({ steps, activeStep, skipped, completedSteps, onStepClick }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    
    // Handle clicking on a step
    const handleStepClick = (index) => {
        // Allow clicking on steps that are completed or the next available step
        // Don't check activeStep for completed steps
        if (!completedSteps.has(index) && index !== activeStep && index > activeStep) {
            // Check if all previous steps are either completed or skipped
            const canNavigate = Array.from({length: index}).every((_, i) => 
                i === activeStep || completedSteps.has(i) || skipped.has(i)
            );
            
            if (!canNavigate) {
                return;
            }
        }
        
        // Update URL with section parameter based on the step
        let sectionParam = '';
        switch(index) {
            case 0:
                sectionParam = 'provisioning';
                break;
            case 1:
                sectionParam = 'termination';
                break;
            case 2:
                sectionParam = 'uar';
                break;
            case 3:
                sectionParam = 'admin';
                break;
            default:
                sectionParam = '';
        }
        
        // Update URL with the section parameter
        if (sectionParam) {
            navigate(`/applications/controls/${id}?section=${sectionParam}`, { replace: true });
        } else {
            navigate(`/applications/controls/${id}`, { replace: true });
        }
        
        // Call the callback to update the active step
        onStepClick(index);
        
        // Use our enhanced scroll to top function
        scrollToTop();
    };
    
  return (
    <mui.Stepper 
      activeStep={activeStep} 
      sx={{ 
                '& .MuiStepLabel-root': {
                    color: 'rgba(0, 0, 0, 0.54)',
                },
                '& .MuiStepIcon-root': {
                    color: 'rgba(0, 0, 0, 0.54)',
                },
                '& .MuiStepIcon-root.Mui-active': {
            color: '#1976d2',
          },
                '& .MuiStepIcon-root.Mui-completed': {
                    color: '#2e7d32',
                },
      }}
    >
      {steps.map((step, index) => {
        const stepProps = {};
        const labelProps = {};
        
        if (skipped.has(index)) {
          stepProps.completed = false;
        } else if (completedSteps && completedSteps.has(index)) {
          stepProps.completed = true;
        }
        
        // Determine if this step is clickable
        // A step is clickable if it's:
        // 1. The current step
        // 2. A completed step
        // 3. Next in sequence after all previous steps are completed/skipped
        const isClickable = index === activeStep || 
                           completedSteps.has(index) || 
                           (index === activeStep + 1 && 
                            Array.from({length: index}).every((_, i) => 
                              completedSteps.has(i) || skipped.has(i)
                            ));
        
        return (
          <mui.Step 
            key={step.label} 
            {...stepProps}
            onClick={() => handleStepClick(index)}
            style={{ 
              cursor: isClickable ? 'pointer' : 'not-allowed',
              opacity: isClickable ? 1 : 0.7
            }}
          >
            <mui.StepLabel {...labelProps}>
              {step.label}
            </mui.StepLabel>
          </mui.Step>
        );
      })}
    </mui.Stepper>
  );
};

const MyApplicationControls = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Add scroll position tracking state
    const [showScrollTop, setShowScrollTop] = useState(false);
    
    // Initialize state with localStorage values
    const [activeStep, setActiveStep] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_activeStep`);
        const initialStep = saved ? parseInt(saved, 10) : 0;
        console.log(`Initial activeStep: ${initialStep} (from localStorage: ${saved})`);
        return initialStep;
    });
    
    // Create a wrapped setActiveStep function that logs changes
    const setActiveStepWithLogging = (newStep) => {
        console.log(`Setting activeStep to: ${newStep}, previous value: ${activeStep}`);
        setActiveStep(newStep);
    };
    
    const [skipped, setSkipped] = useState(() => {
        // For Set type, we need to convert from stored array back to Set
        const savedSkipped = localStorage.getItem(`app_${id}_skipped`);
        return savedSkipped ? new Set(JSON.parse(savedSkipped)) : new Set();
    });
    
    const [completedSteps, setCompletedSteps] = useState(() => {
        const savedCompleted = localStorage.getItem(`app_${id}_completed`);
        return savedCompleted ? new Set(JSON.parse(savedCompleted)) : new Set();
    });
    
    // Check URL for section parameter to set initial active step
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sectionParam = urlParams.get('section');
        
        if (sectionParam) {
            // Map section parameter to the corresponding step index
            let newStep = 0;
            switch(sectionParam) {
                case 'provisioning':
                    newStep = 0; // Provisioning is step 0
                    break;
                case 'termination':
                    newStep = 1; // Termination is step 1
                    break;
                case 'uar':
                    newStep = 2; // User Access Review is step 2
                    break;
                case 'admin':
                    newStep = 3; // Privileged Access is step 3
                    break;
                default:
                    newStep = 0; // Default to first step
            }
            
            // Only update if it's different from current active step
            if (newStep !== activeStep) {
                setActiveStepWithLogging(newStep);
            }
        }
    }, [id]); // Run when id changes
    
    const [initialChoiceMade, setInitialChoiceMade] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_initialChoiceMade`);
        return saved ? JSON.parse(saved) : false;
    });
    
    const [appData, setAppData] = useState({});
    const [selectedApps, setSelectedApps] = useState({});
    const [selectedAppsbyCompany, setSelectedAppsbyCompany] = useState({});
    const [selectedApptoCopy, setSelectedApptoCopy] = useState(null);
    const [copyProvisioning, setCopyProvisioning] = useState(false);
    const [copyTermination, setCopyTermination] = useState(false);
    const [copyUAR, setCopyUAR] = useState(false);
    const [copyAdmin, setCopyAdmin] = useState(false);
    const [copyAppName, setCopyAppName] = useState(null);
    
    // Save to localStorage when state changes
    useEffect(() => {
        localStorage.setItem(`app_${id}_activeStep`, activeStep.toString());
    }, [activeStep, id]);
    
    useEffect(() => {
        // Convert Set to Array for storage
        localStorage.setItem(`app_${id}_skipped`, JSON.stringify(Array.from(skipped)));
    }, [skipped, id]);
    
    useEffect(() => {
        localStorage.setItem(`app_${id}_initialChoiceMade`, JSON.stringify(initialChoiceMade));
    }, [initialChoiceMade, id]);

    // Add a state to track if data is loaded 
    const [dataLoaded, setDataLoaded] = useState(false);

    //PROVISIONING
    const [recordExist, setRecordExist] = useState(false);
    const [provisioningData, setProvisioningData] = useState([]);
    const [selectedForm, setSelectedForm] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState([]);
    const [selectedGrantor, setSelectedGrantor] = useState([]);
    const [selectedProcessDiff, setSelectedProcessDiff] = useState([]);
    const [selectedProcessDiffOther, setSelectedProcessDiffOther] = useState([]);

    //TERMINATION
    const [termrecordExist, setTermRecordExist] = useState(false);
    const [terminationData, setTerminationData] = useState([]);
    const [selectedNetReliance, setSelectedNetReliance] = useState([]);
    const [selectedTermDoc, setSelecteTermDoc] = useState([]);
    const [selectedTermNotifyer, setSelecteTermNotifyer] = useState([]);
    const [selectedTermProcess, setSelectedTermProcess] = useState([]);
    const [selectedDisableType, setSelectedDisableType] = useState([]);
    const [selectedTermDiff, setSelectedTermDiff] = useState([]);
    const [selectedTermDiffOther, setSelectedTermDiffOther] = useState([]);

    //UAR
    const [uarrecordExist, setUARRecordExist] = useState(false);
    const [uarData, setUARData] = useState([]);
    const [frequency, setFrequency] = useState([]);
    const [scopeusers, setScopeUsers] = useState([]);
    const [scoperoles, setScopeRoles] = useState([]);
    const [extractor, setExtractor] = useState([]);
    const [istool, setTool] = useState([]);
    const [SOD, setSOD] = useState([]);
    const [extractsend, setExtractSend] = useState([]);
    const [reviewer, setReviewer] = useState([]);
    const [cnareviewer, setCNAReviewer] = useState([]);
    const [changedoc, setChangeDoc] = useState([]);
    const [changedocsend, setChangeDocSend] = useState([]);
    const [changedocreviewed, setChangeDocReviewed] = useState([]);

    //ADMIN
    const [adminrecordExist, setAdminRecordExist] = useState(false);
    const [adminData, setAdminData] = useState([]);
    const [adminfrequency, setAdminFrequency] = useState([]);
    const [adminroles, setAdminRoles] = useState([]);
    const [adminrolesOption, setAdminRolesOption] = useState([]);
    const [admincapabilities, setAdminCapabilities] = useState([]);
    const [adminreview, setAdminReview] = useState([]);
    const [adminreviewdoc, setAdminReviewDoc] = useState([]);

    // Add saving indicator states
    const [isSaving, setIsSaving] = useState(false);
    const [saveComplete, setSaveComplete] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);

    const saveTimeout = useRef(null);

    const [openCopyModal, setOpenCopyModal] = useState(false);
    
    // Menu state for Quick Jump
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    // Define steps for the questionnaire
    const steps = [
      { 
        label: 'Access Provisioning', 
        description: 'Configure how user access is provisioned in your application.',
        isRequired: true 
      },
      { 
        label: 'User Termination', 
        description: 'Configure how user access is terminated in your application.',
        isRequired: true 
      },
      { 
        label: 'User Access Review', 
        description: 'Define how user access is periodically reviewed.',
        isRequired: true 
      },
      { 
        label: 'Privileged Accounts', 
        description: 'Configure privileged account management for your application.',
        isRequired: true 
      },
      { 
        label: 'Change Management', 
        description: 'Configure change management processes (optional).',
        isRequired: false
      }
    ];
    
    // Check if a step is optional
    const isStepOptional = (step) => {
      return step === 4; // Change Management is optional
    };
    
    // Check if a step should be skipped
    const isStepSkipped = (step) => {
      return skipped.has(step);
    };
    
    // Function to check if a step is complete
    const isStepComplete = (step) => {
      switch(step) {
        case 0: 
          return provCheck;
        case 1:
          return termCheck;
        case 2:
          return uarCheck;
        case 3:
          return adminCheck;
        case 4:
          return true; // Change Management is optional
        default:
          return false;
      }
    };
    
    // Handle next step
    const handleNext = async () => {
        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }
        
        // First, ensure we check for completion
        await visibleCheckMark();
        
        // Calculate the next step
        let newStep;
        if (activeStep === steps.length - 1) {
            // Last step - ensure we're moving to the completion screen
            newStep = steps.length; // Set explicitly to steps.length to show completion
            console.log("Finished questionnaire, moving to completion screen");
        } else {
            newStep = activeStep + 1;
        }
        
        // Update URL with section parameter based on the new step (only for actual steps, not completion)
        if (newStep < steps.length) {
            let sectionParam = '';
            switch(newStep) {
                case 0:
                    sectionParam = 'provisioning';
                    break;
                case 1:
                    sectionParam = 'termination';
                    break;
                case 2:
                    sectionParam = 'uar';
                    break;
                case 3:
                    sectionParam = 'admin';
                    break;
                default:
                    sectionParam = '';
            }
            
            // Use navigate to update URL without creating a new history entry
            if (sectionParam) {
                navigate(`/applications/controls/${id}?section=${sectionParam}`, { replace: true });
            } else {
                navigate(`/applications/controls/${id}`, { replace: true });
            }
        }
        
        // Move to the next step
        setActiveStepWithLogging(newStep);
        setSkipped(newSkipped);
        
        // Use our enhanced scroll to top function
        scrollToTop();
    };
    
    // Handler for starting the questionnaire from scratch
    const handleStartQuestionnaire = () => {
        // Mark that the user has made a choice and navigate to first step
        setInitialChoiceMade(true);
        setActiveStepWithLogging(0);
    };
    
    // Handler for importing an existing process
    const handleImportProcess = () => {
        // Just open the modal, don't change the active step yet
        // The step will be changed only after successful copy in copyProcess
        handleOpenCopyModal();
    };
    
    // Handle back step
    const handleBack = () => {
        const newStep = activeStep - 1;
        
        // Update URL with section parameter based on the new step
        let sectionParam = '';
        switch(newStep) {
            case 0:
                sectionParam = 'provisioning';
                break;
            case 1:
                sectionParam = 'termination';
                break;
            case 2:
                sectionParam = 'uar';
                break;
            case 3:
                sectionParam = 'admin';
                break;
            default:
                sectionParam = '';
        }
        
        // Use history.replace to update URL without creating a new history entry
        if (sectionParam) {
            navigate(`/applications/controls/${id}?section=${sectionParam}`, { replace: true });
        } else {
            navigate(`/applications/controls/${id}`, { replace: true });
        }
        
        setActiveStepWithLogging(newStep);
        
        // Use our enhanced scroll to top function
        scrollToTop();
    };
    
    // Handle returning to My Applications page
    const handleReturnToApplications = async () => {
      // Make sure to check and update the process status before navigating away
      try {
          await visibleCheckMark();
          console.log("Process status check completed, navigating to applications");
          navigate('/applications');
      } catch (error) {
          console.error("Error updating process status:", error);
          // Navigate anyway after error to prevent user from being stuck
          navigate('/applications');
      }
    };
    
    // Handle skip step
    const handleSkip = () => {
      if (!isStepOptional(activeStep)) {
        throw new Error("You can't skip a step that isn't optional.");
      }
      
      setActiveStepWithLogging((prevActiveStep) => prevActiveStep + 1);
      setSkipped((prevSkipped) => {
        const newSkipped = new Set(prevSkipped.values());
        newSkipped.add(activeStep);
        return newSkipped;
      });
    };

    const handleOpenCopyModal = () => {
        setOpenCopyModal(true);
    };

    const handleCloseCopyModal = (event, reason) => {
        if (reason === 'backdropClick') {
            // Do nothing when backdrop is clicked, keeping the modal open
            return;
        }
        // Close modal for other cases like clicking the cancel button
        setOpenCopyModal(false);
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [isCopying, setIsCopying] = useState(false);

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const copyProcess = async (e) => {
        e.preventDefault();
        setIsCopying(true);

        // Add validation to check if any process is selected
        if (!copyProvisioning && !copyTermination && !copyUAR && !copyAdmin) {
            setSnackbarMessage('Please select at least one process to import');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setIsCopying(false);
            return;
        }

        try {

        if(copyProvisioning === true) {

            try {
                //Provisioning
                const provisioningCopy = await appService.getProvisioningProcessByID(copyAppName);

                if(provisioningCopy) {

                    setProvisioningData(provisioningCopy);

                      // Safely split the strings
                      const formArray = safeSplit(provisioningCopy.FORM);
                      const approvalArray = safeSplit(provisioningCopy.APPROVERS);
                      const grantorArray = safeSplit(provisioningCopy.GRANTOR);
                      const processArray = provisioningCopy.PROCESS_DIFFERENCE
                     
                      const form = formArray.map(item => ({
                          value: item,
                          label: item,
                      }));
  
                      setSelectedForm(form);
      
                      const approver = approvalArray.map(item => ({
                          value: item,
                          label: item,
                      }));
                      setSelectedApprover(approver);
      
                      const grantor = grantorArray.map(item => ({
                          value: item,
                          label: item,
                      }));
                      setSelectedGrantor(grantor);
  
                      const process = {
                          value: provisioningCopy.PROCESS_DIFFERENCE,
                          label: provisioningCopy.PROCESS_DIFFERENCE
                      };
                      setSelectedProcessDiff(process)

                      const updatedData = {
                        ...provisioningCopy,
                        APP_NAME: id,
                    };

                    saveChanges(updatedData)

                }
                

            } catch (fetchError) {
                console.error('Error copying provisioning details:', fetchError);
            }

        }

        if(copyTermination === true) {

            try {
                
                //Termination Copy
                const terminationCopy = await appService.getTerminationProcessByID(copyAppName);

                if(terminationCopy) {

                    const netRelianceArray = safeSplit(terminationCopy.NETWORK_RELIANCE);
                    const documentationArray = safeSplit(terminationCopy.TERM_DOCUMENTATION);
                    const termprocessArray = safeSplit(terminationCopy.TERM_PROCESS);
                    const termprocessdiffArray = safeSplit(terminationCopy.PROCESS_DIFFERENCE);
                    const disabletypeArray = safeSplit(terminationCopy.DISABLE_TYPE);
                    const notifyertypeArray = safeSplit(terminationCopy.TERM_NOTIFYER);
                    
                    // Create a new object that includes all fields including "OTHER" fields
                    const completeTerminationData = {
                        ...terminationCopy,
                        TERM_DOCUMENTATION_OTHER: terminationCopy.TERM_DOCUMENTATION_OTHER || '',
                        TERM_NOTIFYER_OTHER: terminationCopy.TERM_NOTIFYER_OTHER || '',
                        TERM_PROCESS_OTHER: terminationCopy.TERM_PROCESS_OTHER || '',
                        DISABLE_TYPE_OTHER: terminationCopy.DISABLE_TYPE_OTHER || '',
                        PROCESS_DIFFERENCE_OTHER: terminationCopy.PROCESS_DIFFERENCE_OTHER || '',
                        TERM_AUTOMATED_DESC: terminationCopy.TERM_AUTOMATED_DESC || ''
                    };
                    
                    setTerminationData(completeTerminationData)

                    const netreliance = netRelianceArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedNetReliance(netreliance);

                    const termnotifyer = notifyertypeArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelecteTermNotifyer(termnotifyer);

                    const termdoc = documentationArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelecteTermDoc(termdoc);

                    const termprocess = termprocessArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedTermProcess(termprocess);

                    const termprocessdiff = termprocessdiffArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedTermDiff(termprocessdiff);

                    const disabletype = disabletypeArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedDisableType(disabletype);

                    const updatedData = {
                        ...terminationCopy,
                        APP_NAME: id,
                        // Explicitly include all OTHER fields
                        TERM_DOCUMENTATION_OTHER: terminationCopy.TERM_DOCUMENTATION_OTHER || '',
                        TERM_NOTIFYER_OTHER: terminationCopy.TERM_NOTIFYER_OTHER || '',
                        TERM_PROCESS_OTHER: terminationCopy.TERM_PROCESS_OTHER || '',
                        DISABLE_TYPE_OTHER: terminationCopy.DISABLE_TYPE_OTHER || '',
                        PROCESS_DIFFERENCE_OTHER: terminationCopy.PROCESS_DIFFERENCE_OTHER || '',
                        TERM_AUTOMATED_DESC: terminationCopy.TERM_AUTOMATED_DESC || ''
                    };

                    saveTermChanges(updatedData);

                    // Skip initial choice if data already exists and we're not in the initial choice state
                    if (activeStep === -1) {
                        // Only auto advance if this isn't the first load and there's no saved step
                        if (initialChoiceMade && !localStorage.getItem(`app_${id}_activeStep`)) {
                            setActiveStepWithLogging(0);
                        }
                    }
                }

               
            } catch (fetchError) {
                console.error('Error copying termination details:', fetchError);
            }
            
        }

        if(copyUAR === true) {

            try {
                
                //UAR Copy
                const UARCopy = await appService.getUARProcessByID(copyAppName);

                if (UARCopy) {

                    setUARData(UARCopy)

                    setUARRecordExist(true)

                    const frequencyArray = safeSplit(UARCopy.FREQUENCY)
                    const scopeuserArray = safeSplit(UARCopy.SCOPE_USERS)
                    const scoperolesArray = safeSplit(UARCopy.SCOPE_ROLES)
                    const extractSendArray = safeSplit(UARCopy.EXTRACTION_SEND)
                    const extractorArray = safeSplit(UARCopy.EXTRACTOR)
                    const reviewerArray = safeSplit(UARCopy.REVIEWER)
                    const cnaReviewerArray = safeSplit(UARCopy.CNA_REVIEWER)
                    const changeDocArray = safeSplit(UARCopy.CHANGE_DOCUMENTATION)
                    const changeDocSendArray = safeSplit(UARCopy.CHANGE_DOCUMENTATION_SEND)
                    const changeDocReviewedArray = safeSplit(UARCopy.CHANGE_DOCUMENTATION_REVIEWED)

                    const frequency = frequencyArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setFrequency(frequency);

                    const scopeusers = scopeuserArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setScopeUsers(scopeusers);

                    const scoperoles = scoperolesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setScopeRoles(scoperoles);

                    const extractor = extractorArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setExtractor(extractor);

                    const istool = {
                        value: UARCopy.IS_TOOL,
                        label: UARCopy.IS_TOOL
                    };
                    setTool(istool)

                    const SOD = {
                        value: UARCopy.SOD_CHECK,
                        label: UARCopy.SOD_CHECK
                    };
                    setSOD(SOD)

                    const extractsend = extractSendArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setExtractSend(extractsend);

                    const reviewer = reviewerArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setReviewer(reviewer);

                    const cnareviewer = cnaReviewerArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setCNAReviewer(cnareviewer);

                    const changedoc = changeDocArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDoc(changedoc);

                    const changedocsend = changeDocSendArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDocSend(changedocsend);

                    const changedocreviewed = changeDocReviewedArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDocReviewed(changedocreviewed);
                    
                    const updatedData = {
                        ...UARCopy,
                        APP_NAME: id,
                    };
 
                    saveUARChanges(updatedData);
                    
                    // Skip initial choice if data already exists and we're not in the initial choice state
                    if (activeStep === -1) {
                        // Only auto advance if this isn't the first load and there's no saved step
                        if (initialChoiceMade && !localStorage.getItem(`app_${id}_activeStep`)) {
                            setActiveStepWithLogging(0);
                        }
                    }
                }

            } catch (fetchError) {
                console.error('Error copying UAR details:', fetchError);
            }
            
        }

        if(copyAdmin === true) {

            try {
                
                //Admin  Copy
                const adminCopy = await appService.getAdminProcessByID(copyAppName);

                if (adminCopy) { 


                    const admincapabilitiesArray = safeSplit(adminCopy.CAPABILITIES)
                    const adminfrequencyArray = safeSplit(adminCopy.ADMIN_REVIEW_FREQUENCY)
                    const adminreviewdocArray = safeSplit(adminCopy.ADMIN_REVIEW_DOCUMENT)
                    const adminrolesArray = safeSplit(adminCopy.ADMIN_ROLES)
                 

                    setAdminRecordExist(true)

                    setAdminData(adminCopy)

                    const capabilities = admincapabilitiesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminCapabilities(capabilities);


                    const adminreview = {
                        value: adminCopy.ADMIN_REVIEW_PERFORMED,
                        label: adminCopy.ADMIN_REVIEW_PERFORMED
                    };
                    setAdminReview(adminreview)

                    const frequency = adminfrequencyArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminFrequency(frequency);

                    const adminreviewdoc = adminreviewdocArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminReviewDoc(adminreviewdoc);

                    const adminroles = adminrolesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminRoles(adminroles);

                    const updatedData = {
                        ...adminCopy,
                        APP_NAME: id,
                    };

                    saveAdminChanges(updatedData)

                }

            } catch (fetchError) {
                console.error('Error copying Admin details:', fetchError);
            }
            
        }

        setSnackbarMessage('Process successfully copied');
        setSnackbarSeverity('success');
        setSnackbarOpen(true); 
        handleCloseCopyModal();
        
        // Mark that the user has made a choice and navigate to first step
        setInitialChoiceMade(true);
        setActiveStepWithLogging(0);

        } catch (error) {
        setSnackbarMessage('There was a problem copying existing process]');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        } finally {
            setIsCopying(false);
        }

    };

   
    // Fetch the selected app
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch apps by ID
                const appsByIdResponse = await appService.fetchAppsById(id);
                setSelectedApps(appsByIdResponse);

                const appList = await appService.fetchAppsByCompany(appsByIdResponse?.COMPANY_ID);

                // Filter the appList to exclude the item with the given `id`
                const filteredAppList = appList.filter(app => app.id !== id);
                
                // Map the filtered list to create a new array with `value` and `label` properties
                const filteredList = filteredAppList.map(item => ({
                    value: item.id,
                    label: item.APP_NAME,
                }));
                
                setSelectedAppsbyCompany(filteredList);
                

            } catch (fetchError) {
                console.error('Error fetching apps details:', fetchError);
                setSelectedApps([]); 
                setSelectedAppsbyCompany([]);
            }
        };

        fetchData();

    }, [id]); 

    const safeSplit = (value, delimiter = ',') => {
        if (value && typeof value === 'string') {
            return value.split(delimiter);
        }
        return [];
    };
    // Fetch the selected app
    useEffect(() => {
        const fetchProv = async () => {
            try {
                // Fetch apps by ID
                const provisioningRecord = await appService.getProvisioningProcessByID(id);

                if (provisioningRecord) {

                    setProvisioningData(provisioningRecord);
            
                   // Safely split the strings
                    const formArray = safeSplit(provisioningRecord.FORM);
                    const approvalArray = safeSplit(provisioningRecord.APPROVERS);
                    const grantorArray = safeSplit(provisioningRecord.GRANTOR);
                    const processArray = provisioningRecord.PROCESS_DIFFERENCE
                   
                    const form = formArray.map(item => ({
                        value: item,
                        label: item,
                    }));

                    setSelectedForm(form);
    
                    const approver = approvalArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedApprover(approver);
    
                    const grantor = grantorArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedGrantor(grantor);

                    const process = {
                        value: provisioningRecord.PROCESS_DIFFERENCE,
                        label: provisioningRecord.PROCESS_DIFFERENCE
                    };
                    setSelectedProcessDiff(process)
    

                    setRecordExist(true)
                    
                    // Skip initial choice if data already exists and we're not in the initial choice state
                    if (activeStep === -1) {
                        // Only auto advance if this isn't the first load and there's no saved step
                        if (initialChoiceMade && !localStorage.getItem(`app_${id}_activeStep`)) {
                            setActiveStepWithLogging(0);
                        }
                    }

                } 
                    
            } catch (error) {
                console.error('Error fetching prov:', error); 
            }
        };

        const fetchTerm = async () => {
            try {
                const terminationRecord = await appService.getTerminationProcessByID(id)

                if (terminationRecord) {
                    const netRelianceArray = safeSplit(terminationRecord.NETWORK_RELIANCE);
                    const documentationArray = safeSplit(terminationRecord.TERM_DOCUMENTATION);
                    const termprocessArray = safeSplit(terminationRecord.TERM_PROCESS);
                    const termprocessdiffArray = safeSplit(terminationRecord.PROCESS_DIFFERENCE);
                    const disabletypeArray = safeSplit(terminationRecord.DISABLE_TYPE);
                    const notifyertypeArray = safeSplit(terminationRecord.TERM_NOTIFYER);
                    
                    // Ensure TIMELINESS is properly handled
                    const timelinessValue = terminationRecord.TIMELINESS || '';
                    console.log(`Loaded TIMELINESS value: ${timelinessValue}, type: ${typeof timelinessValue}`);
                    
                    // Create a complete termination data object including all fields
                    const completeTerminationData = {
                        ...terminationRecord,
                        TERM_DOCUMENTATION_OTHER: terminationRecord.TERM_DOCUMENTATION_OTHER || '',
                        TERM_NOTIFYER_OTHER: terminationRecord.TERM_NOTIFYER_OTHER || '',
                        TERM_PROCESS_OTHER: terminationRecord.TERM_PROCESS_OTHER || '',
                        DISABLE_TYPE_OTHER: terminationRecord.DISABLE_TYPE_OTHER || '',
                        PROCESS_DIFFERENCE_OTHER: terminationRecord.PROCESS_DIFFERENCE_OTHER || '',
                        TERM_AUTOMATED_DESC: terminationRecord.TERM_AUTOMATED_DESC || '',
                        TIMELINESS: timelinessValue
                    };
                    
                    console.log("Loaded termination data:", completeTerminationData);
                    
                    setTerminationData(completeTerminationData);

                    const netreliance = netRelianceArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedNetReliance(netreliance);

                    const termnotifyer = notifyertypeArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelecteTermNotifyer(termnotifyer);

                    const termdoc = documentationArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelecteTermDoc(termdoc);

                    const termprocess = termprocessArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedTermProcess(termprocess);

                    const termprocessdiff = termprocessdiffArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedTermDiff(termprocessdiff);

                    const disabletype = disabletypeArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setSelectedDisableType(disabletype);

                    const updatedData = {
                        ...terminationRecord,
                        APP_NAME: id,
                        // Explicitly include all OTHER fields
                        TERM_DOCUMENTATION_OTHER: terminationRecord.TERM_DOCUMENTATION_OTHER || '',
                        TERM_NOTIFYER_OTHER: terminationRecord.TERM_NOTIFYER_OTHER || '',
                        TERM_PROCESS_OTHER: terminationRecord.TERM_PROCESS_OTHER || '',
                        DISABLE_TYPE_OTHER: terminationRecord.DISABLE_TYPE_OTHER || '',
                        PROCESS_DIFFERENCE_OTHER: terminationRecord.PROCESS_DIFFERENCE_OTHER || '',
                        TERM_AUTOMATED_DESC: terminationRecord.TERM_AUTOMATED_DESC || ''
                    };

                    saveTermChanges(updatedData);
                    
                    // Skip initial choice if data already exists and we're not in the initial choice state
                    if (activeStep === -1) {
                        // Only auto advance if this isn't the first load and there's no saved step
                        if (initialChoiceMade && !localStorage.getItem(`app_${id}_activeStep`)) {
                            setActiveStepWithLogging(0);
                        }
                    }
                }
            
            
            } catch (fetchError) {
                console.error('Error fetching termed', fetchError);  
            }
        };

        const fetchUAR = async () => {
            try {
                console.log("Fetching UAR data for app ID:", id);
                const uarRecord = await appService.getUARProcessByID(id);
                console.log("UAR data received from API:", uarRecord);
               
                if (uarRecord) {
                    console.log("Setting UAR data:", uarRecord);
                    
                    // Set the main UAR data state
                    setUARData(uarRecord);
                    setUARRecordExist(true);

                    // Parse comma-separated values into arrays for dropdowns
                    const frequencyArray = safeSplit(uarRecord.FREQUENCY);
                    const scopeuserArray = safeSplit(uarRecord.SCOPE_USERS)
                    const scoperolesArray = safeSplit(uarRecord.SCOPE_ROLES)
                    const extractSendArray = safeSplit(uarRecord.EXTRACTION_SEND)
                    const extractorArray = safeSplit(uarRecord.EXTRACTOR)
                    const reviewerArray = safeSplit(uarRecord.REVIEWER)
                    const cnaReviewerArray = safeSplit(uarRecord.CNA_REVIEWER)
                    const changeDocArray = safeSplit(uarRecord.CHANGE_DOCUMENTATION)
                    const changeDocSendArray = safeSplit(uarRecord.CHANGE_DOCUMENTATION_SEND)
                    const changeDocReviewedArray = safeSplit(uarRecord.CHANGE_DOCUMENTATION_REVIEWED)

                    console.log("Parsed UAR arrays:", {
                        frequency: frequencyArray,
                        scopeusers: scopeuserArray,
                        scoperoles: scoperolesArray
                    });

                    // Format single-value frequency as a dropdown option object
                    // Frequency should be a single selection, not an array
                    const frequencyValue = frequencyArray.length > 0 ? frequencyArray[0] : '';
                    const frequency = frequencyValue ? {
                        value: frequencyValue,
                        label: frequencyValue
                    } : null;
                    console.log("Setting frequency to:", frequency);
                    setFrequency(frequency);

                    const scopeusers = scopeuserArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setScopeUsers(scopeusers);

                    const scoperoles = scoperolesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setScopeRoles(scoperoles);

                    const extractor = extractorArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setExtractor(extractor);

                    // Format single select values
                    const istool = {
                        value: uarRecord.IS_TOOL,
                        label: uarRecord.IS_TOOL
                    };
                    setTool(istool);

                    const SODValue = {
                        value: uarRecord.SOD_CHECK,
                        label: uarRecord.SOD_CHECK
                    };
                    setSOD(SODValue);

                    const extractsend = extractSendArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setExtractSend(extractsend);

                    const reviewer = reviewerArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setReviewer(reviewer);

                    const cnareviewer = cnaReviewerArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setCNAReviewer(cnareviewer);

                    const changedoc = changeDocArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDoc(changedoc);

                    const changedocsend = changeDocSendArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDocSend(changedocsend);

                    const changedocreviewed = changeDocReviewedArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setChangeDocReviewed(changedocreviewed);
                    
                    const updatedData = {
                        ...uarData,
                        APP_NAME: id,
                    };
  
                    saveUARChanges(updatedData);
                    
                    // Skip initial choice if data already exists
                    if (!localStorage.getItem(`app_${id}_activeStep`)) {
                        setActiveStepWithLogging(0);
                    }
                } else {
                    console.log("No UAR data found for app ID:", id);
                    // Initialize with empty data if none exists
                    setUARData({});
                    setUARRecordExist(false);
                }
            } catch (error) {
                console.error('Error fetching UAR data:', error);  
                // Initialize with empty data on error
                setUARData({});
                setUARRecordExist(false);
            }
        };

        const fetchAdmin = async () => {
            try {

                const AppRecord = await appService.fetchAppsRecordById(id)

                if (AppRecord) {
                    //Get the active users
                    const roles = AppRecord.filter(user => user.ROLE_NAME && user.ROLE_NAME.toLowerCase() !== '');
                    const uniqueRoles = Array.from(new Set(roles.map(user => user.ROLE_NAME)));

                    const uniqueroles = uniqueRoles.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminRolesOption(uniqueroles);
                } else {
                    setAdminRolesOption([]);
                }

                const adminRecord = await appService.getAdminProcessByID(id)

                if (adminRecord) { 


                    const admincapabilitiesArray = safeSplit(adminRecord.CAPABILITIES)
                    const adminfrequencyArray = safeSplit(adminRecord.ADMIN_REVIEW_FREQUENCY)
                    const adminreviewdocArray = safeSplit(adminRecord.ADMIN_REVIEW_DOCUMENT)
                    const adminrolesArray = safeSplit(adminRecord.ADMIN_ROLES)
                 

                    setAdminRecordExist(true)

                    setAdminData(adminRecord)

                    const capabilities = admincapabilitiesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminCapabilities(capabilities);


                    const adminreview = {
                        value: adminRecord.ADMIN_REVIEW_PERFORMED,
                        label: adminRecord.ADMIN_REVIEW_PERFORMED
                    };
                    setAdminReview(adminreview)

                    const frequency = adminfrequencyArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminFrequency(frequency);

                    const adminreviewdoc = adminreviewdocArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminReviewDoc(adminreviewdoc);

                    const adminroles = adminrolesArray.map(item => ({
                        value: item,
                        label: item,
                    }));
                    setAdminRoles(adminroles);
                    
                    const updatedData = {
                        ...adminRecord,
                        APP_NAME: id,
                    };

                    saveAdminChanges(updatedData)

                }

            } catch (fetchError) {
                console.error('Error fetching Admins:', fetchError);  
            }
        }

        fetchProv()
        fetchTerm()
        fetchUAR()
        fetchAdmin()
    },

    []); 

        //TRIGGER THE CHECKMARK
        useEffect(() => {
            // Run the check in an async IIFE
            (async () => {
                try {
                    await visibleCheckMark();
                } catch (error) {
                    console.error('Error checking process status:', error);
                }
            })();
        }, [selectedApps, provisioningData, terminationData, uarData, adminData]);

    // Add an effect to check completion status when all data loading is complete
    useEffect(() => {
        // Check if all data is loaded and then verify completion status
        if (
            selectedApps && 
            Object.keys(provisioningData).length > 0 && 
            Object.keys(terminationData).length > 0 && 
            Object.keys(uarData).length > 0 && 
            Object.keys(adminData).length > 0
        ) {
            console.log("All data loaded, checking completion status");
            // Mark data as loaded
            setDataLoaded(true);
            
            // Small timeout to ensure all data is processed
            const timer = setTimeout(() => {
                visibleCheckMark();
            }, 500);
            
            return () => clearTimeout(timer);
        }
    }, [recordExist, selectedApps, provisioningData, terminationData, uarData, adminData]);
    
    // Add an effect to ensure scrolling when active step changes
    useEffect(() => {
        // Scroll to top when the active step changes
        if (activeStep >= 0) {
            console.log(`Active step changed to ${activeStep}, scrolling to top`);
            scrollToTop();
        }
    }, [activeStep]);
    
    // This effect ensures activeStep is restored from localStorage after data loads
    useEffect(() => {
        if (dataLoaded) {
            const savedStep = localStorage.getItem(`app_${id}_activeStep`);
            if (savedStep) {
                console.log(`Restoring saved step: ${savedStep}`);
                setActiveStepWithLogging(parseInt(savedStep, 10));
                // Don't scroll here to avoid unexpected scrolls on initial load
            }
        }
    }, [dataLoaded, id]);

    // Add version tracking state variables
    const [versionInfo, setVersionInfo] = useState({
        provisioning: { version: 1, lastUpdated: null, updatedBy: null },
        termination: { version: 1, lastUpdated: null, updatedBy: null },
        uar: { version: 1, lastUpdated: null, updatedBy: null },
        admin: { version: 1, lastUpdated: null, updatedBy: null }
    });
    
    // Function to get current user info - in a real app, this would come from auth context
    const getCurrentUser = () => {
        // This would normally be retrieved from authentication context
        return {
            id: "current-user",
            name: "Current User",
            email: "user@example.com"
        };
    };
    
    // Load version information when component mounts
    useEffect(() => {
        const fetchVersionInfo = async () => {
            try {
                // These would be actual API calls in a real implementation
                const provVersion = await appService.getVersionInfo(id, 'provisioning') || { version: 1, lastUpdated: null, updatedBy: null };
                const termVersion = await appService.getVersionInfo(id, 'termination') || { version: 1, lastUpdated: null, updatedBy: null };
                const uarVersion = await appService.getVersionInfo(id, 'uar') || { version: 1, lastUpdated: null, updatedBy: null };
                const adminVersion = await appService.getVersionInfo(id, 'admin') || { version: 1, lastUpdated: null, updatedBy: null };
                
                setVersionInfo({
                    provisioning: provVersion,
                    termination: termVersion,
                    uar: uarVersion,
                    admin: adminVersion
                });
                
                console.log("Loaded version information:", { provVersion, termVersion, uarVersion, adminVersion });
            } catch (error) {
                console.error("Error fetching version information:", error);
            }
        };
        
        if (id) {
            fetchVersionInfo();
        }
    }, [id]);

    const saveChanges = async (updatedData) => {
        // Skip if another save is already in progress
        if (saveInProgress) {
            return;
        }
        
        setSaveInProgress(true);
        setIsSaving(true);
        setSaveComplete(false);
        
        try {
            // Update version information
            const currentUser = getCurrentUser();
            const currentTime = new Date().toISOString();
            const newVersion = versionInfo.provisioning.version + 1;
            
            // Add version metadata to the data being saved
            const dataWithVersion = {
                ...updatedData,
                VERSION: newVersion,
                LAST_UPDATED: currentTime,
                UPDATED_BY: currentUser.id
            };
            
            if (recordExist) {
                // Update existing record
                const updated_record = await appService.updateProvisioningProcess(id, dataWithVersion);
                setProvisioningData(updated_record);
                setRecordExist(true);
                
                // Update version info in state
                setVersionInfo(prev => ({
                    ...prev,
                    provisioning: {
                        version: newVersion,
                        lastUpdated: currentTime,
                        updatedBy: currentUser.name
                    }
                }));
                
                // Try to save version history if API supports it
                try {
                    await appService.saveVersionHistory(id, 'provisioning', {
                        version: newVersion,
                        data: dataWithVersion,
                        timestamp: currentTime,
                        userId: currentUser.id,
                        userName: currentUser.name
                    });
                } catch (versionError) {
                    console.warn("Version history tracking not available:", versionError);
                }
                
                // Show save complete indicator
                setSaveComplete(true);
                setTimeout(() => {
                    setIsSaving(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                    }, 1000);
                }, 500);
            } else {
                // Create a new record
                if (id) {
                    const newData = { 
                        ...dataWithVersion, 
                        APP_NAME: id,
                        VERSION: 1, // First version for new records
                        LAST_UPDATED: currentTime,
                        UPDATED_BY: currentUser.id
                    };
                    
                    const newRecord = await appService.createProvisioningProcess(newData);
                    setProvisioningData(newRecord);
                    setRecordExist(true);
                    
                    // Update version info in state
                    setVersionInfo(prev => ({
                        ...prev,
                        provisioning: {
                            version: 1,
                            lastUpdated: currentTime,
                            updatedBy: currentUser.name
                        }
                    }));
                    
                    // Show save complete indicator
                    setSaveComplete(true);
                    setTimeout(() => {
                        setIsSaving(false);
                        setTimeout(() => {
                            setSaveComplete(false);
                        }, 1000);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error updating record:', error);
            setIsSaving(false);
            setSaveComplete(false);
        } finally {
            setSaveInProgress(false);
        }
    };

    const saveTermChanges = async (updatedData) => {
        // Skip if another save is already in progress and it's not a forced save
        if (saveInProgress && !updatedData._forceSave) {
            return;
        }
        
        // Delete the internal flag if present
        if (updatedData._forceSave) {
            delete updatedData._forceSave;
        }
        
        // Convert TIMELINESS to string if it's a number
        const timelinessValue = updatedData.TIMELINESS !== undefined 
            ? String(updatedData.TIMELINESS) 
            : (terminationData.TIMELINESS !== undefined ? String(terminationData.TIMELINESS) : '');
        
        // Ensure all OTHER fields are included
        const completeData = {
            ...updatedData,
            TERM_DOCUMENTATION_OTHER: updatedData.TERM_DOCUMENTATION_OTHER || terminationData.TERM_DOCUMENTATION_OTHER || '',
            TERM_NOTIFYER_OTHER: updatedData.TERM_NOTIFYER_OTHER || terminationData.TERM_NOTIFYER_OTHER || '',
            TERM_PROCESS_OTHER: updatedData.TERM_PROCESS_OTHER || terminationData.TERM_PROCESS_OTHER || '',
            DISABLE_TYPE_OTHER: updatedData.DISABLE_TYPE_OTHER || terminationData.DISABLE_TYPE_OTHER || '',
            PROCESS_DIFFERENCE_OTHER: updatedData.PROCESS_DIFFERENCE_OTHER || terminationData.PROCESS_DIFFERENCE_OTHER || '',
            TERM_AUTOMATED_DESC: updatedData.TERM_AUTOMATED_DESC || terminationData.TERM_AUTOMATED_DESC || '',
            TIMELINESS: timelinessValue
        };
        
        setSaveInProgress(true);
        setIsSaving(true);
        setSaveComplete(false);
        
        try {
            // Update version information
            const currentUser = getCurrentUser();
            const currentTime = new Date().toISOString();
            const newVersion = versionInfo.termination.version + 1;
            
            // Add version metadata to the data being saved
            const dataWithVersion = {
                ...completeData,
                VERSION: newVersion,
                LAST_UPDATED: currentTime,
                UPDATED_BY: currentUser.id
            };
            
            if (termrecordExist) {
                // Log the data being sent to the server for debugging
                console.log('Saving termination data:', JSON.stringify(dataWithVersion));
                
                const updated_record = await appService.updateTerminationProcess(id, dataWithVersion);
                
                // Ensure state is updated with the returned data
                setTerminationData(updated_record);
                setTermRecordExist(true);
                
                // Update version info in state
                setVersionInfo(prev => ({
                    ...prev,
                    termination: {
                        version: newVersion,
                        lastUpdated: currentTime,
                        updatedBy: currentUser.name
                    }
                }));
                
                // Try to save version history if API supports it
                try {
                    await appService.saveVersionHistory(id, 'termination', {
                        version: newVersion,
                        data: dataWithVersion,
                        timestamp: currentTime,
                        userId: currentUser.id,
                        userName: currentUser.name
                    });
                } catch (versionError) {
                    console.warn("Version history tracking not available:", versionError);
                }
                
                // Show save complete indicator
                setSaveComplete(true);
                setTimeout(() => {
                    setIsSaving(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                    }, 1000);
                }, 500);
            } else {
                if (id) {
                    const newData = { 
                        ...dataWithVersion, 
                        APP_NAME: id,
                        VERSION: 1, // First version for new records
                        LAST_UPDATED: currentTime,
                        UPDATED_BY: currentUser.id
                    };
                    
                    // Log the data being sent to the server for debugging
                    console.log('Creating termination data:', JSON.stringify(newData));
                    
                    const newRecord = await appService.createTerminationProcess(newData);
                    setTerminationData(newRecord);
                    setTermRecordExist(true);
                    
                    // Update version info in state
                    setVersionInfo(prev => ({
                        ...prev,
                        termination: {
                            version: 1,
                            lastUpdated: currentTime,
                            updatedBy: currentUser.name
                        }
                    }));
                    
                    // Show save complete indicator
                    setSaveComplete(true);
                    setTimeout(() => {
                        setIsSaving(false);
                        setTimeout(() => {
                            setSaveComplete(false);
                        }, 1000);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error updating termination record:', error);
            setIsSaving(false);
            setSaveComplete(false);
        } finally {
            setSaveInProgress(false);
        }
    };

    const saveUARChanges = async (updatedData) => {
        // Skip if another save is already in progress
        if (saveInProgress) {
            return;
        }
        
        setSaveInProgress(true);
        setIsSaving(true);
        setSaveComplete(false);
        
        try {
            // Make sure we have a valid APP_NAME in the data
            // For UAR process, we need to ensure APP_NAME is properly set
            // The API expects the actual APP_NAME, not just the ID
            
            // Update version information
            const currentUser = getCurrentUser();
            const currentTime = new Date().toISOString();
            const newVersion = versionInfo.uar.version + 1;
            
            const dataWithVersion = {
                ...updatedData,
                APP_NAME: id,
                VERSION: newVersion,
                LAST_UPDATED: currentTime,
                UPDATED_BY: currentUser.id
            };

            console.log('Saving UAR data:', dataWithVersion);
            
            if (uarrecordExist) {
                // Update existing record
                const updated_record = await appService.updateUARProcess(id, dataWithVersion);
                console.log('UAR update response:', updated_record);
                
                if (updated_record) {
                    setUARData(updated_record);
                    setUARRecordExist(true);
                    
                    // Update version info in state
                    setVersionInfo(prev => ({
                        ...prev,
                        uar: {
                            version: newVersion,
                            lastUpdated: currentTime,
                            updatedBy: currentUser.name
                        }
                    }));
                    
                    // Try to save version history if API supports it
                    try {
                        await appService.saveVersionHistory(id, 'uar', {
                            version: newVersion,
                            data: dataWithVersion,
                            timestamp: currentTime,
                            userId: currentUser.id,
                            userName: currentUser.name
                        });
                    } catch (versionError) {
                        console.warn("Version history tracking not available:", versionError);
                    }
                    
                    // Show save complete indicator
                    setSaveComplete(true);
                    setTimeout(() => {
                        setIsSaving(false);
                        setTimeout(() => {
                            setSaveComplete(false);
                        }, 1000);
                    }, 500);
                } else {
                    throw new Error('No response from UAR update');
                }
            } else {
                // Create new record
                if (id) {
                    const newData = { 
                        ...dataWithVersion,
                        VERSION: 1, // First version for new records
                        LAST_UPDATED: currentTime,
                        UPDATED_BY: currentUser.id
                    };
                    
                    const newRecord = await appService.createUARProcess(newData);
                    console.log('UAR create response:', newRecord);
                    
                    if (newRecord) {
                        setUARData(newRecord);
                        setUARRecordExist(true);
                        
                        // Update version info in state
                        setVersionInfo(prev => ({
                            ...prev,
                            uar: {
                                version: 1,
                                lastUpdated: currentTime,
                                updatedBy: currentUser.name
                            }
                        }));
                        
                        // Show save complete indicator
                        setSaveComplete(true);
                        setTimeout(() => {
                            setIsSaving(false);
                            setTimeout(() => {
                                setSaveComplete(false);
                            }, 1000);
                        }, 500);
                    } else {
                        throw new Error('No response from UAR creation');
                    }
                }
            }
        } catch (error) {
            console.error('Error updating UAR record:', error);
            setIsSaving(false);
            setSaveComplete(false);
        } finally {
            setSaveInProgress(false);
        }
    };

    const saveAdminChanges = async (updatedData) => {
        // Skip if another save is already in progress
        if (saveInProgress) {
            return;
        }
        
        setSaveInProgress(true);
        setIsSaving(true);
        setSaveComplete(false);
        
        try {
            // Update version information
            const currentUser = getCurrentUser();
            const currentTime = new Date().toISOString();
            const newVersion = versionInfo.admin.version + 1;
            
            const dataWithVersion = {
                ...updatedData,
                VERSION: newVersion,
                LAST_UPDATED: currentTime,
                UPDATED_BY: currentUser.id
            };
            
            if (adminrecordExist) {
                const updated_record = await appService.updateAdminProcess(id, dataWithVersion);
                setAdminData(updated_record);
                setAdminRecordExist(true);
                
                // Update version info in state
                setVersionInfo(prev => ({
                    ...prev,
                    admin: {
                        version: newVersion,
                        lastUpdated: currentTime,
                        updatedBy: currentUser.name
                    }
                }));
                
                // Try to save version history if API supports it
                try {
                    await appService.saveVersionHistory(id, 'admin', {
                        version: newVersion,
                        data: dataWithVersion,
                        timestamp: currentTime,
                        userId: currentUser.id,
                        userName: currentUser.name
                    });
                } catch (versionError) {
                    console.warn("Version history tracking not available:", versionError);
                }
                
                // Show save complete indicator
                setSaveComplete(true);
                setTimeout(() => {
                    setIsSaving(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                    }, 1000);
                }, 500);
            } else {
                if (id) {
                    const newData = { 
                        ...dataWithVersion, 
                        APP_NAME: id,
                        VERSION: 1, // First version for new records
                        LAST_UPDATED: currentTime,
                        UPDATED_BY: currentUser.id 
                    };
                    
                    const newRecord = await appService.createAdminProcess(newData);
                    setAdminData(newRecord);
                    setAdminRecordExist(true);
                    
                    // Update version info in state
                    setVersionInfo(prev => ({
                        ...prev,
                        admin: {
                            version: 1,
                            lastUpdated: currentTime,
                            updatedBy: currentUser.name
                        }
                    }));
                    
                    // Show save complete indicator
                    setSaveComplete(true);
                    setTimeout(() => {
                        setIsSaving(false);
                        setTimeout(() => {
                            setSaveComplete(false);
                        }, 1000);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error updating admin record:', error);
            setIsSaving(false);
            setSaveComplete(false);
        } finally {
            setSaveInProgress(false);
        }
    };

    const isNonEmptyString = (value) => {
        if (typeof value === 'number') {
            return value > 0; 
        }
        // Handle arrays (for multi-select fields)
        if (Array.isArray(value)) {
            return value.length > 0 && value.some(item => 
                (typeof item === 'string' && item.trim() !== '') ||
                (typeof item === 'object' && item && item.value && item.value.trim() !== '')
            );
        }
        return typeof value === 'string' && value.trim() !== '';
    };
    const isValidInteger = (value) => Number.isInteger(value) && value > 0;
    
    // Initialize the check states from localStorage
    const [provCheck, setProvCheck] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_provCheck`);
        return saved ? JSON.parse(saved) : false;
    });
    
    const [termCheck, setTermCheck] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_termCheck`);
        return saved ? JSON.parse(saved) : false;
    });
    
    const [uarCheck, setUARCheck] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_uarCheck`);
        return saved ? JSON.parse(saved) : false;
    });
    
    const [adminCheck, setAdminCheck] = useState(() => {
        const saved = localStorage.getItem(`app_${id}_adminCheck`);
        return saved ? JSON.parse(saved) : false;
    });
    
    // Save check states to localStorage when they change
    useEffect(() => {
        localStorage.setItem(`app_${id}_provCheck`, JSON.stringify(provCheck));
    }, [provCheck, id]);
    
    useEffect(() => {
        localStorage.setItem(`app_${id}_termCheck`, JSON.stringify(termCheck));
    }, [termCheck, id]);
    
    useEffect(() => {
        localStorage.setItem(`app_${id}_uarCheck`, JSON.stringify(uarCheck));
    }, [uarCheck, id]);
    
    useEffect(() => {
        localStorage.setItem(`app_${id}_adminCheck`, JSON.stringify(adminCheck));
    }, [adminCheck, id]);

    const saveAppChanges = async (updatedData) => {
        try {
            // Wait for the update to complete
            const response = await appService.updateApp(id, updatedData);
            console.log('App status updated successfully:', response);
            return response;
        } catch (error) {
            console.error('Error saving application changes:', error);
            return null;
        }
    };

    useEffect(() => {
        visibleCheckMark();
    }, [selectedApps, provisioningData, terminationData, uarData, adminData]);

    const visibleCheckMark = async () => {
        let provisioningValid = false;
        let terminationValid = false;
        let uarValid = false;
        let adminValid = false;

        //PROVISIONING
        if (
            isNonEmptyString(provisioningData?.FORM) &&
            isNonEmptyString(provisioningData?.APPROVERS) &&
            isNonEmptyString(provisioningData?.GRANTOR) &&
            isNonEmptyString(provisioningData?.PROCESS_DIFFERENCE)
        ) {
            setProvCheck(true)
            provisioningValid = true;
        } else {
            setProvCheck(false)
            provisioningValid = false;
        }

        //TERMINATION   
        if (
            isNonEmptyString(terminationData?.TERM_NOTIFYER) &&
            isNonEmptyString(terminationData?.NETWORK_RELIANCE) &&
            isNonEmptyString(terminationData?.TIMELINESS) &&
            isNonEmptyString(terminationData?.TERM_DOCUMENTATION) &&
            isNonEmptyString(terminationData?.TERM_PROCESS) &&
            isNonEmptyString(terminationData?.TERM_AUTOMATED_DESC) &&
            isNonEmptyString(terminationData?.DISABLE_TYPE) 
        ) {
            setTermCheck(true)
            terminationValid = true;
        } else {
            setTermCheck(false)
            terminationValid = false;
        }

         //UAR   
         if (
            isNonEmptyString(uarData?.FREQUENCY) &&
            isNonEmptyString(uarData?.DAYS_TO_COMPLETE) &&
            isNonEmptyString(uarData?.SCOPE_USERS) &&
            isNonEmptyString(uarData?.SCOPE_ROLES) &&
            isNonEmptyString(uarData?.EXTRACTOR) &&
            isNonEmptyString(uarData?.IS_TOOL) &&
            isNonEmptyString(uarData?.EXTRACTION_SEND) &&
            isNonEmptyString(uarData?.REVIEWER) &&
            isNonEmptyString(uarData?.CNA_REVIEWER) &&
            isNonEmptyString(uarData?.CNA_PROCESS) &&
            isNonEmptyString(uarData?.CHANGE_DOCUMENTATION) &&
            isNonEmptyString(uarData?.CHANGE_DOCUMENTATION_SEND) &&
            isNonEmptyString(uarData?.CHANGE_TIMELINE) &&
            isNonEmptyString(uarData?.CHANGE_DOCUMENTATION_REVIEWED) &&
            isNonEmptyString(uarData?.SOD_CHECK)
        ) {
            setUARCheck(true);
            uarValid = true;
            console.log('UAR validation passed. All required fields are present.');
        } else {
            setUARCheck(false);
            uarValid = false;
            console.log('UAR validation failed. Missing required fields:', {
                FREQUENCY: uarData?.FREQUENCY,
                DAYS_TO_COMPLETE: uarData?.DAYS_TO_COMPLETE,
                SCOPE_USERS: uarData?.SCOPE_USERS,
                SCOPE_ROLES: uarData?.SCOPE_ROLES,
                EXTRACTOR: uarData?.EXTRACTOR,
                IS_TOOL: uarData?.IS_TOOL,
                EXTRACTION_SEND: uarData?.EXTRACTION_SEND,
                REVIEWER: uarData?.REVIEWER,
                CNA_REVIEWER: uarData?.CNA_REVIEWER,
                CNA_PROCESS: uarData?.CNA_PROCESS,
                CHANGE_DOCUMENTATION: uarData?.CHANGE_DOCUMENTATION,
                CHANGE_DOCUMENTATION_SEND: uarData?.CHANGE_DOCUMENTATION_SEND,
                CHANGE_TIMELINE: uarData?.CHANGE_TIMELINE,
                CHANGE_DOCUMENTATION_REVIEWED: uarData?.CHANGE_DOCUMENTATION_REVIEWED,
                SOD_CHECK: uarData?.SOD_CHECK
            });
        }

        //ADMIN 
        if (
            isNonEmptyString(adminData?.ADMIN_ROLES) &&
            isNonEmptyString(adminData?.CAPABILITIES) &&
            isNonEmptyString(adminData?.ADMIN_REVIEW_PERFORMED) &&
            isNonEmptyString(adminData?.ADMIN_REVIEW_FREQUENCY) &&
            isNonEmptyString(adminData?.ADMIN_REVIEW_DOCUMENT) 
        ) {
            setAdminCheck(true)
            adminValid = true;
        } else {
            setAdminCheck(false)
            adminValid = false;
        }

        const isProcessValid = provisioningValid && terminationValid && uarValid && adminValid;

        // Check if the current SETUP_PROCESS state is different from what it should be
        // This prevents unnecessary updates and UI flickers
        if (isProcessValid !== (selectedApps?.SETUP_PROCESS === true)) {
            const updatedData = {
                ...selectedApps,
                SETUP_PROCESS: isProcessValid
            };
            
            // Only save if we have a valid selectedApps object
            if (selectedApps && selectedApps.id) {
                console.log(`Updating SETUP_PROCESS to ${isProcessValid}`);
                try {
                    await saveAppChanges(updatedData);
                } catch (error) {
                    console.error('Failed to update process status:', error);
                }
            }
        }
    };

    //PROVISIONING OPTIONS
    const documentationOptions = [
        { value: 'Email', label: 'Email' },
        { value: 'Ticketing System', label: 'Ticketing System' },
        { value: 'Verbal', label: 'Verbal' },
    ];

    const approverOptions = [
        { value: 'Immediate Superior/Manager or Equivalent', label: 'Immediate Superior/Manager or Equivalent' },
        { value: 'Role Owner', label: 'Role Owner' },
        { value: 'IT System Owner', label: 'IT System Owner' },
        { value: 'Other', label: 'Other' },
    ];

    const grantorOptions = [
        { value: 'System Administrator', label: 'System Administrator' },
        { value: 'Vendor', label: 'Vendor' },
        { value: 'Automated', label: 'Automated' },
        { value: 'Other', label: 'Other' },
    ];

    const YesNoOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

     //TERMINATION OPTIONS
    const termed_documentationOptions = [
        { value: 'Email', label: 'Email' },
        { value: 'Ticketing System', label: 'Ticketing System' },
        { value: 'Verbal', label: 'Verbal' },
    ];

    const termed_notifyer = [
        { value: 'HR', label: 'HR' },
        { value: 'Immediate Superior/Manager', label: 'Immediate Superior/Manager' },
    ];

    const revokeMethodOptions = [
        { value: 'Manual', label: 'Manual' },
        { value: 'Automated', label: 'Automated' },
    ];

    const networkrelianceOptions = [
        { value: 'Yes - Full Reliance', label: 'Yes - Full Reliance' },
        { value: 'Yes - Partial Reliance', label: 'Yes - Partial Reliance' },
        { value: 'No - Not Relying', label: 'No - Not Relying' },
    ];

    const revokeOptions = [
        { value: 'Deactivated', label: 'Deactivated' },
        { value: 'Deleted', label: 'Deleted' },
    ];


    //UAR PROCESS
    const frequencyOptions = [
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Bi-Weekly', label: 'Bi-Weekly' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Semi-Annually', label: 'Semi-Annually' },
        { value: 'Annually', label: 'Annually' },
    ];

    const scopeusersOptions = [
        { value: 'Employees', label: 'Employees' },
        { value: 'Contractors', label: 'Contractors' },
        { value: 'Vendor Accounts', label: 'Vendor Accounts' },
        { value: 'System Accounts', label: 'System Accounts' },
    ];

    const scoperolesOptions = [
        { value: 'Role Assignments', label: 'Role Assignments' },
        { value: 'Roles and Permissions', label: 'Roles and Permissions' },
    ];

    const reviewOptions = [
        { value: 'Users Supervisor/Manager or equivalent', label: 'Users Supervisor/Manager or equivalent' },
        { value: 'Roles Owners', label: 'Roles Owners' },
        { value: 'System Owners', label: 'System Owners' },
    ];

    const cnareviewOptions = [
        { value: 'Users Supervisor/Manager or equivalent', label: 'Users Supervisor/Manager or equivalent' },
        { value: 'Roles Owners', label: 'Roles Owners' },
        { value: 'System Owners', label: 'System Owners' },
        { value: 'System Administrators', label: 'System Administrators' },
    ];

    const extractorOptions = [
        { value: 'System Administrators', label: 'System Administrators' },
        { value: 'Vendors', label: 'Vendors' },
    ];

    const toolOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    const changerequestdocOptions = [
        { value: 'Added Column in the User Access Report', label: 'Added Column in the User Access Report' },
        { value: 'Within the Tool used in UAR', label: 'Within the Tool used in UAR' },
        { value: 'Email', label: 'Email' },
        { value: 'Ticketing System', label: 'Ticketing System' },
        { value: 'Chat', label: 'Chat' },
    ];

    const changecommunicationOptions = [
        { value: 'Email', label: 'Email' },
        { value: 'Ticketing System', label: 'Ticketing System' },
        { value: 'Chat', label: 'Chat' },
        { value: 'Verbal', label: 'Verbal' },
    ];

    const changeactiondocOptions = [
        { value: 'Screenshot attached in the UAR documentation', label: 'Screenshot attached in the UAR documentation' },
        { value: 'Downloaded updated user list', label: 'Downloaded updated user list' },
        { value: 'Email confirmation about the actions taken', label: 'Email confirmation about the actions taken' },
        { value: 'Attached as support within the ticket', label: 'Attached as support within the ticket' },
    ];

    const SODYesNoOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    //ADMIN PROCESS
    const capabilitiesOptions = [
        { value: 'Manage User Access (create;  update; and delete)', label: 'Manage User Access (create, update, and delete)'},
        { value: 'Manage System Configuration', label: 'Manage System Configuration' },
        { value: 'Develop Changes in Production', label: 'Develop Changes in Production' },
        { value: 'Implement Changes in Production', label: 'Implement Changes in Production' },
    ];

    const AdminYesNoOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    const adminreviewdocOptions = [
        { value: 'Added Column in the Admin Activity Report', label: 'Added Column in the Admin Activity Report' },
        { value: 'Reviewed as part of the UAR', label: 'Reviewed as part of the UAR' },
        { value: 'Email', label: 'Email' },
        { value: 'Ticketing System', label: 'Ticketing System' },
        { value: 'Chat', label: 'Chat' },
    ];


    const handleFormChange = (selectedOptions) => {
        const formatted_form = selectedOptions.map(option => option.value);
        const forms = formatted_form.join(',');

        setSelectedForm(selectedOptions)
    
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Update the state with the new FORM value
        const updatedData = {
            ...provisioningData,
            FORM: forms
        };
        saveChanges(updatedData)
    };

    const handleApprovalChange = (selectedOptions) => {

        const formatted_approval = selectedOptions.map(option => option.value);
        const approver = formatted_approval.join(',');

        setSelectedApprover(selectedOptions)

            const updatedData = {
                ...provisioningData,
                APPROVERS: approver
            };
     
        saveChanges(updatedData)

    };

    const handleGrantorChange = (selectedOptions) => {
        const formatted_grantor = selectedOptions.map(option => option.value);
        const grantor = formatted_grantor.join(',');

        setSelectedGrantor(selectedOptions)

            const updatedData = {
                ...provisioningData,
                GRANTOR: grantor
            };
            // Save changes with the updated state

            saveChanges(updatedData)
    };

    const handleProcessDiffChange = (selectedOptions) => {

        const process = selectedOptions.value
        setSelectedProcessDiff(selectedOptions)
        
            const updatedData = {
                ...provisioningData,
                PROCESS_DIFFERENCE: process
            };
            // Save changes with the updated state
            saveChanges(updatedData)

    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        const updatedData = {
            ...provisioningData,
            [name]: value
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with the updated state
        saveTimeout.current = setTimeout(() => {
            saveChanges(updatedData);
        }, 1000); 

        setProvisioningData(updatedData)
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...provisioningData,
            [name]: value
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        saveChanges(updatedData);
        setProvisioningData(updatedData);
    };

    //TERMATION SECTION
    const handleRelianceChange = (selectedOptions) => {
        let reliance;
        
        // Handle all possible input formats
        if (Array.isArray(selectedOptions)) {
            // For multi-select or array format
            const formatted_reliance = selectedOptions.map(option => option.value);
            reliance = formatted_reliance.join(',');
            setSelectedNetReliance(selectedOptions);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select (object with value property)
            reliance = selectedOptions.value;
            // Set as an array with a single item to maintain consistency
            setSelectedNetReliance([selectedOptions]);
        } else {
            // Handle empty selection
            reliance = '';
            setSelectedNetReliance([]);
        }
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        const updatedData = {
            ...terminationData,
            NETWORK_RELIANCE: reliance
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    }

    const handleTermDocChange = (selectedOptions) => {
        // Handle empty selection case
        if (!selectedOptions || selectedOptions.length === 0) {
            setSelecteTermDoc([]);
            
            const updatedData = {
                ...terminationData,
                TERM_DOCUMENTATION: '' // Empty string for no selection
            };
            
            // Save changes with the updated state
            saveTermChanges(updatedData);
            return;
        }
        
        const formatted_termdoc = selectedOptions.map(option => option.value);
        const termdoc = formatted_termdoc.join(',');

        setSelecteTermDoc(selectedOptions);
        
        const updatedData = {
            ...terminationData,
            TERM_DOCUMENTATION: termdoc
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    }

    const handleTermNotifyerChange = (selectedOptions) => {
        // Handle empty selection case
        if (!selectedOptions || selectedOptions.length === 0) {
            setSelecteTermNotifyer([]);
            
            const updatedData = {
                ...terminationData,
                TERM_NOTIFYER: '' // Empty string for no selection
            };
            
            // Save changes with the updated state
            saveTermChanges(updatedData);
            return;
        }
        
        const formatted_termnotifyer = selectedOptions.map(option => option.value);
        const termnotifyer = formatted_termnotifyer.join(',');

        setSelecteTermNotifyer(selectedOptions);
        
        const updatedData = {
            ...terminationData,
            TERM_NOTIFYER: termnotifyer
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    }

    const handleTermProcessChange = (selectedOptions) => {
        // Handle empty selection case
        if (!selectedOptions || selectedOptions.length === 0) {
            setSelectedTermProcess([]);
            
            const updatedData = {
                ...terminationData,
                TERM_PROCESS: '' // Empty string for no selection
            };
            
            // Save changes with the updated state
            saveTermChanges(updatedData);
            return;
        }
        
        const formatted_termprocess = selectedOptions.map(option => option.value);
        const termprocess = formatted_termprocess.join(',');

        setSelectedTermProcess(selectedOptions);

        const updatedData = {
            ...terminationData,
            TERM_PROCESS: termprocess
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    }

    const handleDisableTypeChange = (selectedOptions) => {
        // Handle empty selection case
        if (!selectedOptions || selectedOptions.length === 0) {
            setSelectedDisableType([]);
            
            const updatedData = {
                ...terminationData,
                DISABLE_TYPE: '' // Empty string for no selection
            };
            
            // Save changes with the updated state
            saveTermChanges(updatedData);
            return;
        }
        
        const formatted_disabletype = selectedOptions.map(option => option.value);
        const disableType = formatted_disabletype.join(',');

        setSelectedDisableType(selectedOptions);
        
        const updatedData = {
            ...terminationData,
            DISABLE_TYPE: disableType
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    }


    const handleTermedDiffChange = (selectedOptions) => {
        let process;
        
        // Handle all possible input formats
        if (Array.isArray(selectedOptions)) {
            // For multi-select or array format
            const formatted_process = selectedOptions.map(option => option.value);
            process = formatted_process.join(',');
            setSelectedTermDiff(selectedOptions);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select (object with value property)
            process = selectedOptions.value;
            // Set as an array with a single item to maintain consistency
            setSelectedTermDiff([selectedOptions]);
        } else {
            // Handle empty selection
            process = '';
            setSelectedTermDiff([]);
        }
        
        const updatedData = {
            ...terminationData,
            PROCESS_DIFFERENCE: process
        };
        
        // Save changes with the updated state
        saveTermChanges(updatedData);
    };

    const handleTermChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        // Determine if this is an "OTHER" field that should be prioritized
        const isOtherField = name.includes('_OTHER') || name === 'TERM_AUTOMATED_DESC';
        const isTimeliness = name === 'TIMELINESS';
        
        // For TIMELINESS, ensure it's a string
        let processedValue = value;
        if (isTimeliness && value !== '') {
            processedValue = String(value);
            console.log(`Converting TIMELINESS to string: ${processedValue}`);
        }
        
        const updatedData = {
            ...terminationData,
            [name]: processedValue
        };
        
        // Set a flag for forced save if it's an "OTHER" field or TIMELINESS
        if (isOtherField || isTimeliness) {
            updatedData._forceSave = true;
            console.log(`Updating prioritized field ${name} with value:`, processedValue);
        }
        
        // Update the local state immediately
        setTerminationData({...updatedData}); // Clone to ensure state update
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with the updated state - use a shorter timeout for text fields
        saveTimeout.current = setTimeout(() => {
            saveTermChanges(updatedData);
        }, (isOtherField || isTimeliness) ? 300 : 500); // Even shorter timeout for "OTHER" fields and TIMELINESS
    };

    const handleTermBlur = (e) => {
        const { name, value } = e.target;
        
        // Determine if this is an "OTHER" field that should be prioritized
        const isOtherField = name.includes('_OTHER') || name === 'TERM_AUTOMATED_DESC';
        const isTimeliness = name === 'TIMELINESS';
        
        // For TIMELINESS, ensure it's a string
        let processedValue = value;
        if (isTimeliness && value !== '') {
            processedValue = String(value);
            console.log(`Converting TIMELINESS to string on blur: ${processedValue}`);
        }

        const updatedData = {
            ...terminationData,
            [name]: processedValue
        };
        
        // Set a flag for forced save if it's an "OTHER" field or TIMELINESS
        if (isOtherField || isTimeliness) {
            updatedData._forceSave = true;
            console.log(`Saving prioritized field ${name} on blur with value:`, processedValue);
        }
        
        // Update the local state immediately
        setTerminationData({...updatedData}); // Clone to ensure state update
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save immediately on blur
        saveTermChanges(updatedData);
    };

    const handleTermDiffChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        // Determine if this is an "OTHER" field that should be prioritized
        const isOtherField = name.includes('_OTHER') || name === 'TERM_AUTOMATED_DESC';
        const isTimeliness = name === 'TIMELINESS';
        
        // For TIMELINESS, ensure it's a string
        let processedValue = value;
        if (isTimeliness && value !== '') {
            processedValue = String(value);
            console.log(`Converting TIMELINESS to string: ${processedValue}`);
        }
        
        const updatedData = {
            ...terminationData,
            [name]: processedValue
        };
        
        // Set a flag for forced save if it's an "OTHER" field
        if (isOtherField || isTimeliness) {
            updatedData._forceSave = true;
            console.log(`Updating prioritized diff field ${name} with value:`, processedValue);
        }
        
        // Update the local state immediately
        setTerminationData({...updatedData}); // Clone to ensure state update
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with the updated state
        saveTimeout.current = setTimeout(() => {
            saveTermChanges(updatedData);
        }, (isOtherField || isTimeliness) ? 300 : 500);
    };

    const handleTermDiffBlur = (e) => {
        const { name, value } = e.target;
        
        // Determine if this is an "OTHER" field that should be prioritized
        const isOtherField = name.includes('_OTHER') || name === 'TERM_AUTOMATED_DESC';
        const isTimeliness = name === 'TIMELINESS';
        
        // For TIMELINESS, ensure it's a string
        let processedValue = value;
        if (isTimeliness && value !== '') {
            processedValue = String(value);
            console.log(`Converting TIMELINESS to string on blur: ${processedValue}`);
        }
        
        const updatedData = {
            ...terminationData,
            [name]: processedValue
        };
        
        // Set a flag for forced save if it's an "OTHER" field
        if (isOtherField || isTimeliness) {
            updatedData._forceSave = true;
            console.log(`Saving prioritized diff field ${name} on blur with value:`, processedValue);
        }
        
        // Update the local state immediately
        setTerminationData({...updatedData}); // Clone to ensure state update
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save immediately on blur
        saveTermChanges(updatedData);
    };

    //UAR SECTION
    const handleUARChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        // Ensure CHANGE_TIMELINE is never less than 1
        let processedValue = value;
        if (name === 'CHANGE_TIMELINE') {
            const numValue = parseInt(value, 10);
            if (isNaN(numValue) || numValue < 1) {
                processedValue = '1';
                console.log('CHANGE_TIMELINE value adjusted to minimum of 1 day');
            }
        }
        
        // Update local state immediately for responsive UI
        const updatedData = {
            ...uarData,
            [name]: processedValue
        };
        
        // Update state immediately for responsive UI
        setUARData(updatedData);
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with debounce to avoid too many API calls
        saveTimeout.current = setTimeout(() => {
            console.log(`Saving UAR field ${name} with value:`, processedValue);
            saveUARChanges(updatedData);
        }, 1000); 
    };

    const handleUARBlur = (e) => {
        const { name, value } = e.target;

        // Ensure CHANGE_TIMELINE is never less than 1
        let processedValue = value;
        if (name === 'CHANGE_TIMELINE') {
            const numValue = parseInt(value, 10);
            if (isNaN(numValue) || numValue < 1) {
                processedValue = '1';
                console.log('CHANGE_TIMELINE value adjusted to minimum of 1 day on blur');
            }
        }
        
        // Create updated data object
        const updatedData = {
            ...uarData,
            [name]: processedValue
        };
        
        // Update state
        setUARData(updatedData);
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save immediately on blur
        console.log(`Saving UAR field ${name} on blur with value:`, processedValue);
        saveUARChanges(updatedData);
    };

    const handleFrequencyChange = (selectedOptions) => {
        // For single-select cases
        const frequency = selectedOptions?.value || '';
        
        console.log('Setting frequency to:', selectedOptions);
        setFrequency(selectedOptions);
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        const updatedData = {
            ...uarData,
            FREQUENCY: frequency
        };
        
        console.log('Updating UAR data with new frequency:', updatedData);
        
        // Save changes with the updated state
        saveUARChanges(updatedData);
    }

    const handleScopeUsersChange = (selectedOptions) => {
        const formatted_scopeusers = selectedOptions.map(option => option.value);
        const scopeusers = formatted_scopeusers.join(',');

        // Update dropdown state
        setScopeUsers(selectedOptions);
        
        // Prepare updated data including the new scope users
        const updatedData = {
            ...uarData,
            SCOPE_USERS: scopeusers
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Log for debugging
        console.log('Updating scope users:', scopeusers);
        console.log('UAR data before update:', uarData);
        
        // Save changes
        saveUARChanges(updatedData);
    }

    const handleScopeRolesChange = (selectedOptions) => {
        const formatted_scopeuroles = selectedOptions.map(option => option.value);
        const scoperoles = formatted_scopeuroles.join(',');

        // Update dropdown state
        setScopeRoles(selectedOptions);
        
        // Prepare updated data including the new scope roles
        const updatedData = {
            ...uarData,
            SCOPE_ROLES: scoperoles
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Log for debugging
        console.log('Updating scope roles:', scoperoles);
        console.log('UAR data before update:', uarData);
        
        // Save changes
        saveUARChanges(updatedData);
    }

    const handleExtractorChange = (selectedOptions) => {
        const formatted_extractor = selectedOptions.map(option => option.value);
        const extractor = formatted_extractor.join(',');

        setExtractor(selectedOptions)
        
            const updatedData = {
                ...uarData,
                EXTRACTOR: extractor
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleIsToolChange = (selectedOptions) => {
        // Check if selectedOptions is an array (multi-select) or an object (single-select)
        if (Array.isArray(selectedOptions)) {
            const formatted_tool = selectedOptions.map(option => option.value);
            const tool = formatted_tool.join(',');
            
            setTool(selectedOptions);
            
            const updatedData = {
                ...uarData,
                IS_TOOL: tool
            };
            // Save changes with the updated state
            saveUARChanges(updatedData);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select cases
            const tool = selectedOptions.value;
            
            setTool(selectedOptions);
            
            const updatedData = {
                ...uarData,
                IS_TOOL: tool
            };
            // Save changes with the updated state
            saveUARChanges(updatedData);
        }
    }

    const handleExtractSendChange = (selectedOptions) => {
        const formatted_extractsend = selectedOptions.map(option => option.value);
        const extractsend = formatted_extractsend.join(',');

        setExtractSend(selectedOptions)
        
            const updatedData = {
                ...uarData,
                EXTRACTION_SEND: extractsend
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleReviewerChange = (selectedOptions) => {
        const formatted_reviewer = selectedOptions.map(option => option.value);
        const reviewer = formatted_reviewer.join(',');

        setReviewer(selectedOptions)
        
            const updatedData = {
                ...uarData,
                REVIEWER: reviewer
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleCNAReviewerChange = (selectedOptions) => {
        const formatted_cnareviewer = selectedOptions.map(option => option.value);
        const cnareviewer = formatted_cnareviewer.join(',');

        setCNAReviewer(selectedOptions)
        
            const updatedData = {
                ...uarData,
                CNA_REVIEWER: cnareviewer
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleChangeDocChange = (selectedOptions) => {
        const formatted_changedoc = selectedOptions.map(option => option.value);
        const changedoc = formatted_changedoc.join(',');

        setChangeDoc(selectedOptions)
        
            const updatedData = {
                ...uarData,
                CHANGE_DOCUMENTATION: changedoc
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleChangeDocSendChange = (selectedOptions) => {
        const formatted_changedocsend = selectedOptions.map(option => option.value);
        const changedocsend = formatted_changedocsend.join(',');

        setChangeDocSend(selectedOptions)
        
            const updatedData = {
                ...uarData,
                CHANGE_DOCUMENTATION_SEND: changedocsend
            };
            // Save changes with the updated state
            saveUARChanges(updatedData)
    }

    const handleChangeDocReviewedChange = (selectedOptions) => {
        const formatted_changedocreviewed = selectedOptions.map(option => option.value);
        const changedocreviewed = formatted_changedocreviewed.join(',');

        setChangeDocReviewed(selectedOptions);
        
        const updatedData = {
            ...uarData,
            CHANGE_DOCUMENTATION_REVIEWED: changedocreviewed
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with the updated state
        saveUARChanges(updatedData);
    }

    const handleSODChange = (selectedOptions) => {
        // Check if selectedOptions is an array (multi-select) or an object (single-select)
        if (Array.isArray(selectedOptions)) {
            const formatted_sod = selectedOptions.map(option => option.value);
            const sod = formatted_sod.join(',');
            
            setSOD(selectedOptions);
            
            const updatedData = {
                ...uarData,
                SOD_CHECK: sod
            };
            // Save changes with the updated state
            saveUARChanges(updatedData);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select cases
            const sod = selectedOptions.value;
            
            setSOD(selectedOptions);
            
            const updatedData = {
                ...uarData,
                SOD_CHECK: sod
            };
            // Save changes with the updated state
            saveUARChanges(updatedData);
        }
    }

    //ADMIN SECTION

    const handleAdminChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        const updatedData = {
            ...adminData,
            [name]: value
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        // Save changes with the updated state
        saveTimeout.current = setTimeout(() => {
            saveAdminChanges(updatedData);
        }, 1000); 

        setAdminData(updatedData)
    };

    const handleAdminBlur = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...adminData,
            [name]: value
        };
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        saveAdminChanges(updatedData);
        setAdminData(updatedData);
    };

    const handleAdminRolesChange = (selectedOptions) => {
        const formatted_adminroles = selectedOptions.map(option => option.value);
        const adminroles = formatted_adminroles.join(',');

        setAdminRoles(selectedOptions)
        
        // Indicate saving has started
        setIsSaving(true);
        setSaveComplete(false);
        
        const updatedData = {
            ...adminData,
            ADMIN_ROLES: adminroles
        };
        // Save changes with the updated state
        saveAdminChanges(updatedData)
    }

    const handleCapabilitiesChange = (selectedOptions) => {
        const formatted_capabilities = selectedOptions.map(option => option.value);
        const capabilities = formatted_capabilities.join(',');

        setAdminCapabilities(selectedOptions)
        
            const updatedData = {
                ...adminData,
                CAPABILITIES: capabilities
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData)
    }

    const handleAdminReviewChange = (selectedOptions) => {
        // Check if selectedOptions is an array (multi-select) or an object (single-select)
        if (Array.isArray(selectedOptions)) {
            const formatted_adminreview = selectedOptions.map(option => option.value);
            const adminreview = formatted_adminreview.join(',');
            
            setAdminReview(selectedOptions);
            
            const updatedData = {
                ...adminData,
                ADMIN_REVIEW_PERFORMED: adminreview
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select cases
            const adminreview = selectedOptions.value;
            
            setAdminReview(selectedOptions);
            
            const updatedData = {
                ...adminData,
                ADMIN_REVIEW_PERFORMED: adminreview
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData);
        }
    }

    const handleAdminFrequencyChange = (selectedOptions) => {
        // Check if selectedOptions is an array (multi-select) or an object (single-select)
        if (Array.isArray(selectedOptions)) {
            const formatted_adminfrequency = selectedOptions.map(option => option.value);
            const adminfrequency = formatted_adminfrequency.join(',');
            
            setAdminFrequency(selectedOptions);
            
            const updatedData = {
                ...adminData,
                ADMIN_REVIEW_FREQUENCY: adminfrequency
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData);
        } else if (selectedOptions && selectedOptions.value) {
            // For single-select cases
            const adminfrequency = selectedOptions.value;
            
            setAdminFrequency(selectedOptions);
            
            const updatedData = {
                ...adminData,
                ADMIN_REVIEW_FREQUENCY: adminfrequency
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData);
        }
    }

    const handleAdminReviewDocChange = (selectedOptions) => {
        const formatted_adminreviewdoc = selectedOptions.map(option => option.value);
        const adminreviewdoc = formatted_adminreviewdoc.join(',');

        setAdminReviewDoc(selectedOptions)
        
            const updatedData = {
                ...adminData,
                ADMIN_REVIEW_DOCUMENT: adminreviewdoc
            };
            // Save changes with the updated state
            saveAdminChanges(updatedData)
    }

    const handleCopyAppChange = (selectedOptions) => {
        const appName = selectedOptions.value
        setSelectedApptoCopy(selectedOptions)
        setCopyAppName(selectedOptions.value)
    }

    // Add version history modal and state
    const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
    const [versionHistoryType, setVersionHistoryType] = useState(null);
    const [versionHistory, setVersionHistory] = useState([]);
    const [loadingVersionHistory, setLoadingVersionHistory] = useState(false);

    // Function to view version history
    const viewVersionHistory = async (type) => {
        setVersionHistoryType(type);
        setLoadingVersionHistory(true);
        setVersionHistoryOpen(true);
        
        try {
            // Call API to get version history
            const history = await appService.getVersionHistory(id, type);
            setVersionHistory(history || []);
        } catch (error) {
            console.error(`Error fetching version history for ${type}:`, error);
            setVersionHistory([]);
        } finally {
            setLoadingVersionHistory(false);
        }
    };

    // Function to get the section title based on type
    const getSectionTitle = (type) => {
        switch(type) {
            case 'provisioning': return 'Access Provisioning';
            case 'termination': return 'User Termination';
            case 'uar': return 'User Access Review';
            case 'admin': return 'Privileged Accounts';
            default: return 'Control Section';
        }
    };

    // Update the VersionIndicator component to include a button to view history
    const VersionIndicator = ({ version, lastUpdated, updatedBy, type }) => {
        if (!version || version === 1) return null; // Don't show for initial version
        
        // Format date
        const formatDate = (dateString) => {
            if (!dateString) return "Never";
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };
        
        return (
            <mui.Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: alpha('#f0f7ff', 0.7),
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                }}
            >
                <mui.Box sx={{ display: 'flex', alignItems: 'center', fontStyle: 'italic' }}>
                    <HistoryEduRoundedIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'primary.main' }} />
                    <mui.Typography variant="caption" sx={{ mr: 1 }}>
                        Version {version}
                    </mui.Typography>
                    {lastUpdated && (
                        <mui.Typography variant="caption" sx={{ mr: 1 }}>
                            • Last updated: {formatDate(lastUpdated)}
                        </mui.Typography>
                    )}
                    {updatedBy && (
                        <mui.Typography variant="caption">
                            • By: {updatedBy}
                        </mui.Typography>
                    )}
                </mui.Box>
                <mui.Button 
                    size="small" 
                    onClick={() => viewVersionHistory(type)}
                    startIcon={<HistoryEduRoundedIcon fontSize="small" />}
                    sx={{ 
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        py: 0.5
                    }}
                >
                    View History
                </mui.Button>
            </mui.Box>
        );
    };

    // Update renderQuestion to pass type to VersionIndicator
    const renderQuestion = () => {
      switch(activeStep) {
        case 0:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Access Provisioning
              </QuestionTitle>
              <QuestionDescription>
                Define how users are provisioned access to this application. This includes request methods,
                approval processes, and how access is granted.
              </QuestionDescription>
                        <VersionIndicator 
                            version={versionInfo.provisioning.version}
                            lastUpdated={versionInfo.provisioning.lastUpdated}
                            updatedBy={versionInfo.provisioning.updatedBy}
                            type="provisioning"
                        />
              <AccessProvisioningTab
                selectedApps={selectedApps}
                provisioningData={provisioningData}
                selectedForm={selectedForm}
                selectedApprover={selectedApprover}
                selectedGrantor={selectedGrantor}
                selectedProcessDiff={selectedProcessDiff}
                documentationOptions={documentationOptions}
                approverOptions={approverOptions}
                grantorOptions={grantorOptions}
                YesNoOptions={YesNoOptions}
                handleOpenCopyModal={handleOpenCopyModal}
                handleFormChange={handleFormChange}
                handleApprovalChange={handleApprovalChange}
                handleGrantorChange={handleGrantorChange}
                handleProcessDiffChange={handleProcessDiffChange}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            </>
          );
          
        case 1:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                User Termination
              </QuestionTitle>
              <QuestionDescription>
                Define how user access is revoked upon termination. This includes notification processes, 
                revocation methods, and documentation.
              </QuestionDescription>
                        <VersionIndicator 
                            version={versionInfo.termination.version}
                            lastUpdated={versionInfo.termination.lastUpdated}
                            updatedBy={versionInfo.termination.updatedBy}
                            type="termination"
                        />
              <UserTerminationTab
                selectedApps={selectedApps}
                terminationData={terminationData}
                selectedNetReliance={selectedNetReliance}
                selectedTermDoc={selectedTermDoc}
                selectedTermNotifyer={selectedTermNotifyer}
                selectedTermProcess={selectedTermProcess}
                selectedDisableType={selectedDisableType}
                selectedTermDiff={selectedTermDiff}
                YesNoOptions={YesNoOptions}
                networkrelianceOptions={networkrelianceOptions}
                handleOpenCopyModal={handleOpenCopyModal}
                handleRelianceChange={handleRelianceChange}
                handleTermDocChange={handleTermDocChange}
                handleTermNotifyerChange={handleTermNotifyerChange}
                handleTermProcessChange={handleTermProcessChange}
                handleDisableTypeChange={handleDisableTypeChange}
                handleTermedDiffChange={handleTermedDiffChange}
                handleTermChange={handleTermChange}
                handleTermDiffChange={handleTermDiffChange}
                handleTermBlur={handleTermBlur}
                handleTermDiffBlur={handleTermDiffBlur}
              />
            </>
          );
          
        case 2:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                User Access Review
              </QuestionTitle>
              <QuestionDescription>
                Define the periodic review process for user access. This includes frequency, scope,
                and how changes are implemented.
              </QuestionDescription>
                        <VersionIndicator 
                            version={versionInfo.uar.version}
                            lastUpdated={versionInfo.uar.lastUpdated}
                            updatedBy={versionInfo.uar.updatedBy}
                            type="uar"
                        />
              <UserAccessReviewTab 
                    selectedApps={selectedApps}
                    uarData={uarData}
                    frequency={frequency}
                    scopeusers={scopeusers}
                    scoperoles={scoperoles}
                    extractor={extractor}
                    istool={istool}
                    SOD={SOD}
                    extractsend={extractsend}
                    reviewer={reviewer}
                    cnareviewer={cnareviewer}
                    changedoc={changedoc}
                    changedocsend={changedocsend}
                    changedocreviewed={changedocreviewed}
                    frequencyOptions={frequencyOptions}
                    scopeusersOptions={scopeusersOptions}
                    scoperolesOptions={scoperolesOptions}
                    extractorOptions={extractorOptions}
                    toolOptions={toolOptions}
                    documentationOptions={changerequestdocOptions}
                    reviewOptions={reviewOptions}
                    cnareviewOptions={cnareviewOptions}
                    handleOpenCopyModal={handleOpenCopyModal}
                    handleFrequencyChange={handleFrequencyChange}
                    handleUARChange={handleUARChange}
                    handleUARBlur={handleUARBlur}
                    handleScopeUsersChange={handleScopeUsersChange}
                    handleScopeRolesChange={handleScopeRolesChange}
                    handleExtractorChange={handleExtractorChange}
                    handleIsToolChange={handleIsToolChange}
                    handleExtractSendChange={handleExtractSendChange}
                    handleReviewerChange={handleReviewerChange}
                    handleCNAReviewerChange={handleCNAReviewerChange}
                    handleChangeDocChange={handleChangeDocChange}
                    handleChangeDocSendChange={handleChangeDocSendChange}
                    handleChangeDocResolutionChange={handleChangeDocReviewedChange}
                    handleSODChange={handleSODChange}
                />
            </>
          );
          
        case 3:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Privileged Accounts
              </QuestionTitle>
              <QuestionDescription>
                Define how privileged accounts are managed. This includes who has administrative access,
                what capabilities they have, and how they are reviewed.
              </QuestionDescription>
                        <VersionIndicator 
                            version={versionInfo.admin.version}
                            lastUpdated={versionInfo.admin.lastUpdated}
                            updatedBy={versionInfo.admin.updatedBy}
                            type="admin"
                        />
              <PrivilegedAccountsTab
                selectedApps={selectedApps}
                adminData={adminData}
                adminroles={adminroles}
                adminrolesOption={adminrolesOption}
                admincapabilities={admincapabilities}
                adminreview={adminreview}
                adminfrequency={adminfrequency}
                adminreviewdoc={adminreviewdoc}
                capabilitiesOptions={capabilitiesOptions}
                AdminYesNoOptions={AdminYesNoOptions}
                frequencyOptions={frequencyOptions}
                adminreviewdocOptions={adminreviewdocOptions}
                handleOpenCopyModal={handleOpenCopyModal}
                handleAdminRolesChange={handleAdminRolesChange}
                handleCapabilitiesChange={handleCapabilitiesChange}
                handleAdminReviewChange={handleAdminReviewChange}
                handleAdminFrequencyChange={handleAdminFrequencyChange}
                handleAdminReviewDocChange={handleAdminReviewDocChange}
                handleAdminChange={handleAdminChange}
                handleAdminBlur={handleAdminBlur}
              />
            </>
          );
          
        case 4:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Change Management
              </QuestionTitle>
              <QuestionDescription>
                Define how changes to the application are managed. This section is optional but recommended
                for a complete security profile.
              </QuestionDescription>
              <ChangeManagementTab />
            </>
          );
          
        default:
          return (
            <mui.Typography>Unknown step</mui.Typography>
          );
      }
    };

    const customMainContent = (
        <div id="application-controls-content" style={{ height: '100%', overflow: 'auto' }}>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link 
                            underline="hover" 
                            color="inherit" 
                            onClick={async (e) => {
                                e.preventDefault();
                                // Make sure to save the process status before navigating
                                await visibleCheckMark();
                                navigate('/applications');
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            My Applications
                        </mui.Link>
                        <mui.Typography color="text.primary">{selectedApps.APP_NAME}</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <GridViewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                {selectedApps.APP_NAME}
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Manage, review, and setup relevant access controls
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    {/* Navigation links */}
                    <mui.Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
                        <mui.Button 
                            variant="contained" 
                            color="primary" 
                            disableElevation
                            startIcon={<LockOutlined />}
                            sx={{ 
                                borderRadius: '8px',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1565c0',
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                                }
                            }}
                        >
                            Access Controls
                        </mui.Button>
                        
                        <mui.Button 
                            variant="outlined" 
                            color="primary"
                            startIcon={<HistoryEduRoundedIcon />}
                            onClick={() => navigate(`/processnarrative/${id}`)}
                            sx={{ 
                                borderRadius: '8px',
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: alpha('#1976d2', 0.04),
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                                }
                            }}
                        >
                            Process Narrative
                        </mui.Button>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
                        p: 3
                    }}>
                        <mui.Box sx={{ mb: 3 }}>
                            <mui.Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}>
                                Access Control Configuration
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Configure access control settings for this application. Complete all sections to ensure proper security compliance.
                            </mui.Typography>
                        </mui.Box>

                        {/* Show initial choice if active step is -1 */}
                        {activeStep === -1 ? (
                            <mui.Box 
                                sx={{ 
                                    mt: 4, 
                                    p: 4, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                }}
                            >
                                <mui.Typography variant="h5" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
                                    How would you like to set up your Access Controls?
                                </mui.Typography>
                                <mui.Typography sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}>
                                    You can either import settings from an existing application or configure each section manually.
                                </mui.Typography>
                                <mui.Box sx={{ display: 'flex', gap: 3, maxWidth: '600px', width: '100%' }}>
                                    <mui.Card 
                                        sx={{ 
                                            flex: 1, 
                                            p: 3, 
                                            borderRadius: 2, 
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { 
                                                transform: 'translateY(-4px)', 
                                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)' 
                                            }
                                        }}
                                        onClick={handleImportProcess}
                                    >
                                        <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                            <CopyAllRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                            <mui.Typography variant="h6" sx={{ mb: 1 }}>
                                                Import Existing Process
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                Copy settings from another application to save time
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Card>
                                    <mui.Card 
                                        sx={{ 
                                            flex: 1, 
                                            p: 3, 
                                            borderRadius: 2, 
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { 
                                                transform: 'translateY(-4px)', 
                                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)' 
                                            }
                                        }}
                                        onClick={handleStartQuestionnaire}
                                    >
                                        <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                            <QuizIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                            <mui.Typography variant="h6" sx={{ mb: 1 }}>
                                                Configure Manually
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                Set up each section step by step through our guided questionnaire
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Card>
                                </mui.Box>
                            </mui.Box>
                        ) : (
                        <>
                            <CompletionStatus 
                                completedCount={(provCheck ? 1 : 0) + (termCheck ? 1 : 0) + (uarCheck ? 1 : 0) + (adminCheck ? 1 : 0)} 
                                totalCount={4} // Excluding the optional Change Management tab
                            />
                         
                            {/* Questionnaire layout */}
                            {activeStep === steps.length ? (
                                // Questionnaire completed
                                <mui.Box sx={{ mt: 4, textAlign: 'center' }}>
                                    {console.log("Rendering completion screen - activeStep:", activeStep, "steps.length:", steps.length)}
                                    <mui.Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                                        All steps completed
                                    </mui.Typography>
                                    <mui.Typography sx={{ mb: 4 }}>
                                        You've successfully completed all required access control configuration steps.
                                    </mui.Typography>
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                        <mui.Button onClick={() => setActiveStepWithLogging(0)} variant="outlined">
                                            Review Configuration
                                        </mui.Button>
                                        <mui.Button 
                                            onClick={handleReturnToApplications} 
                                            variant="contained" 
                                            color="primary"
                                            startIcon={<NavigateBeforeIcon />}
                                            sx={{ 
                                                fontWeight: 500,
                                                boxShadow: '0 4px 10px rgba(4, 111, 178, 0.2)',
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                py: 1.5,
                                                px: 3,
                                                fontSize: '1rem'
                                            }}
                                        >
                                            Return to Applications
                                        </mui.Button>
                                    </mui.Box>
                                </mui.Box>
                            ) : (
                                // Show steps
                                <>
                                    {console.log("Rendering steps screen - activeStep:", activeStep, "steps.length:", steps.length)}
                                    
                                    {/* Quick Jump Menu */}
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                        <mui.Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowDropDownIcon />}
                                            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                                            sx={{ 
                                                borderRadius: '18px', 
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                borderColor: alpha('#000', 0.12),
                                                '&:hover': {
                                                    backgroundColor: alpha('#000', 0.03),
                                                    borderColor: alpha('#000', 0.2),
                                                }
                                            }}
                                        >
                                            Quick Jump
                                        </mui.Button>
                                        <mui.Menu
                                            id="quick-jump-menu"
                                            anchorEl={menuAnchorEl}
                                            open={Boolean(menuAnchorEl)}
                                            onClose={() => setMenuAnchorEl(null)}
                                        >
                                            {steps.map((step, index) => {
                                                // Determine if this step is clickable
                                                // A step is clickable if it's:
                                                // 1. The current step
                                                // 2. A completed step
                                                // 3. Next in sequence after all previous steps are completed/skipped
                                                const isClickable = index === activeStep || 
                                                                   completedSteps.has(index) || 
                                                                   (index === activeStep + 1 && 
                                                                    Array.from({length: index}).every((_, i) => 
                                                                      completedSteps.has(i) || skipped.has(i)
                                                                    ));
                                                
                                                return (
                                                <mui.MenuItem
                                                    key={step.label}
                                                        disabled={!isClickable}
                                                    onClick={() => {
                                                            if (isClickable) {
                                                            setActiveStepWithLogging(index);
                                                            setMenuAnchorEl(null);
                                                                scrollToTop();
                                                        }
                                                    }}
                                                    sx={{ 
                                                        minWidth: '220px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        color: activeStep === index ? 'primary.main' : 'text.primary',
                                                        fontWeight: activeStep === index ? 500 : 400,
                                                            opacity: isClickable ? 1 : 0.7
                                                    }}
                                                >
                                                    {isStepComplete(index) && (
                                                        <CheckCircleRoundedIcon 
                                                            fontSize="small" 
                                                            color="success" 
                                                            sx={{ fontSize: '1rem' }} 
                                                        />
                                                    )}
                                                    {!isStepComplete(index) && (
                                                        <mui.Box 
                                                            sx={{ 
                                                                width: '1rem', 
                                                                height: '1rem', 
                                                                borderRadius: '50%',
                                                                bgcolor: activeStep === index ? 'primary.main' : alpha('#000', 0.1),
                                                                display: 'inline-block'
                                                            }} 
                                                        />
                                                    )}
                                                    {step.label}
                                                </mui.MenuItem>
                                                );
                                            })}
                                        </mui.Menu>
                                    </mui.Box>
                                    
                                    {/* Sticky progress bar */}
                                    <mui.Box
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                            backgroundColor: 'white',
                                            padding: '16px 0',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            marginBottom: '24px',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                        }}
                                    >
                                        <StepProgressBar 
                                            steps={steps} 
                                            activeStep={activeStep} 
                                            skipped={skipped} 
                                            completedSteps={completedSteps}
                                            onStepClick={setActiveStepWithLogging} 
                                        />
                                    </mui.Box>
                                    
                                    <QuestionnaireCard>
                                        {renderQuestion()}
                                        
                                        <NavigationButtons>
                                            <mui.Button
                                                variant="outlined"
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                startIcon={<NavigateBeforeIcon />}
                                            >
                                                Back
                                            </mui.Button>
                                            
                                            <mui.Box>
                                                {isStepOptional(activeStep) && (
                                                    <mui.Button
                                                        color="inherit"
                                                        onClick={handleSkip}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Skip
                                                    </mui.Button>
                                                )}
                                                
                                                <mui.Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    endIcon={activeStep === steps.length - 1 ? null : <NavigateNextIcon />}
                                                    startIcon={isStepComplete(activeStep) ? <CheckCircleRoundedIcon /> : null}
                                                    color={isStepComplete(activeStep) ? "success" : "primary"}
                                                >
                                                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                                </mui.Button>
                                            </mui.Box>
                                        </NavigationButtons>
                                    </QuestionnaireCard>
                                </>
                            )}
                        </>
                        )}

                        <Modal
                            size="lg"
                            open={openCopyModal}
                            onClose={handleCloseCopyModal}
                            header="Process Import Module"
                            body={
                                <>
                                    <mui.Paper 
                                        elevation={0}
                                        sx={{ 
                                            padding: '24px', 
                                            marginTop: '16px',
                                            borderRadius: '12px',
                                            backgroundColor: alpha('#f5f7fa', 0.8),
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <mui.Typography 
                                            variant="subtitle1" 
                                            fontWeight={600}
                                            sx={{ 
                                                marginBottom: '16px',
                                                color: 'primary.main'
                                            }}
                                        >
                                            Source Application
                                        </mui.Typography>

                                        <div style={{ marginBottom: '28px', position: 'relative' }}>
                                            <ModernSelect
                                                label="Select Application"
                                                isMultiSelect={false}
                                                placeholderText="Select Source Application"
                                                selectedOptions={selectedApptoCopy}
                                                selectOptions={selectedAppsbyCompany}
                                                handleChange={handleCopyAppChange}
                                            />
                                        </div>

                                        <mui.Typography 
                                            variant="subtitle1" 
                                            fontWeight={600}
                                            sx={{ 
                                                marginBottom: '16px',
                                                color: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CopyAllRoundedIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    marginRight: '8px',
                                                    color: 'primary.main' 
                                                }} 
                                            />
                                            Select Processes to Import
                                        </mui.Typography>

                                        <mui.Box sx={{ 
                                            '.MuiAccordion-root': {
                                                border: '1px solid',
                                                borderColor: 'divider', 
                                                boxShadow: 'none',
                                                mb: 2,
                                                borderRadius: '8px',
                                                '&::before': {
                                                    display: 'none'
                                                },
                                                '&.Mui-expanded': {
                                                    borderColor: 'primary.light'
                                                }
                                            },
                                            '.MuiAccordionSummary-root': {
                                                borderRadius: '8px',
                                                transition: 'background-color 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: alpha('#e9f0f8', 0.6)
                                                },
                                                '&.Mui-expanded': {
                                                    backgroundColor: alpha('#e9f0f8', 0.8),
                                                    borderBottom: '1px solid',
                                                    borderBottomColor: 'divider'
                                                }
                                            }
                                        }}>
                                            <Accordion sx={{ mt: 2 }}>
                                                <AccordionSummary
                                                    expandIcon={<ArrowDropDownIcon color="primary" />}
                                                    aria-controls="panel1-content"
                                                    id="panel1-header"
                                                >
                                                    <Typography fontWeight={500} sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        color: 'text.primary'
                                                    }}>
                                                        <LockOutlined fontSize="small" 
                                                            sx={{ 
                                                                mr: 1.5,
                                                                color: 'primary.main'
                                                            }} 
                                                        />
                                                        Access Security
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <mui.Box sx={{ ml: 2 }}>
                                                        <FormGroup>
                                                            <FormControlLabel
                                                                control={<Checkbox 
                                                                    onChange={(e) => setCopyProvisioning(e.target.checked)}
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />}
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Access Provisioning
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel
                                                                control={<Checkbox 
                                                                    onChange={(e) => setCopyTermination(e.target.checked)}
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />}
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Access Termination
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel
                                                                control={<Checkbox 
                                                                    onChange={(e) => setCopyUAR(e.target.checked)}
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />}
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        User Access Review
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel
                                                                control={<Checkbox 
                                                                    onChange={(e) => setCopyAdmin(e.target.checked)}
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />}
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Privileged Accounts
                                                                    </mui.Typography>
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </mui.Box>
                                                </AccordionDetails>
                                            </Accordion>
                                            <Accordion>
                                                <AccordionSummary
                                                    expandIcon={<ArrowDropDownIcon color="primary" />}
                                                    aria-controls="panel2-content"
                                                    id="panel2-header"
                                                >
                                                    <Typography fontWeight={500} sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        color: 'text.primary'
                                                    }}>
                                                        <SystemUpdateAlt fontSize="small" 
                                                            sx={{ 
                                                                mr: 1.5,
                                                                color: 'primary.main'
                                                            }} 
                                                        />
                                                        Change Management
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <mui.Box sx={{ ml: 2 }}>
                                                        <FormGroup>
                                                            <FormControlLabel 
                                                                control={<Checkbox 
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />} 
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Change Documentation and Approval
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel 
                                                                control={<Checkbox 
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />} 
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        UAT
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel 
                                                                control={<Checkbox 
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />} 
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Change SOD
                                                                    </mui.Typography>
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </mui.Box>
                                                </AccordionDetails>
                                            </Accordion>
                                            <Accordion>
                                                <AccordionSummary
                                                    expandIcon={<ArrowDropDownIcon color="primary" />}
                                                    aria-controls="panel3-content"
                                                    id="panel3-header"
                                                >
                                                    <Typography fontWeight={500} sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        color: 'text.primary'
                                                    }}>
                                                        <Settings fontSize="small" 
                                                            sx={{ 
                                                                mr: 1.5,
                                                                color: 'primary.main'
                                                            }} 
                                                        />
                                                        IT Operations
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <mui.Box sx={{ ml: 2 }}>
                                                        <FormGroup>
                                                            <FormControlLabel 
                                                                control={<Checkbox 
                                                                    color="primary" 
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />} 
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Back-up and Restoration
                                                                    </mui.Typography>
                                                                }
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <FormControlLabel 
                                                                control={<Checkbox 
                                                                    color="primary"
                                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                />} 
                                                                label={
                                                                    <mui.Typography variant="body2">
                                                                        Job Monitoring
                                                                    </mui.Typography>
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </mui.Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        </mui.Box>
                                    </mui.Paper>
                                </>
                            }
                            footer={
                                <>
                                    <mui.Button 
                                        onClick={handleCloseCopyModal} 
                                        color="inherit" 
                                        sx={{
                                            mb: 2,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                backgroundColor: alpha('#f5f7fa', 0.8),
                                                boxShadow: 'none'
                                            }
                                        }}
                                    >
                                        Cancel
                                    </mui.Button>
                                    <mui.Button 
                                        onClick={copyProcess} 
                                        color="primary" 
                                        variant="contained" 
                                        startIcon={isCopying ? <mui.CircularProgress size={20} color="inherit" /> : <CopyAllRoundedIcon />}
                                        disabled={!selectedApptoCopy || (!copyProvisioning && !copyTermination && !copyUAR && !copyAdmin) || isCopying}
                                        sx={{
                                            mr: 2.5, 
                                            mb: 2,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                                            }
                                        }}
                                    >
                                        {isCopying ? 'Copying...' : 'Import Selected Processes'}
                                    </mui.Button>
                                </>
                            }
                        />
                        
                        {/* Version History Modal */}
                        <Modal
                            size="md"
                            open={versionHistoryOpen}
                            onClose={() => setVersionHistoryOpen(false)}
                            header={`${getSectionTitle(versionHistoryType)} - Version History`}
                            body={
                                <>
                                    {loadingVersionHistory ? (
                                        <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                            <mui.CircularProgress size={32} />
                    </mui.Box>
                                    ) : versionHistory.length === 0 ? (
                                        <mui.Box sx={{ p: 2, textAlign: 'center' }}>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                No version history available for this section.
                                            </mui.Typography>
                </mui.Box>
                                    ) : (
                                        <mui.List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                            {versionHistory.map((version, index) => (
                                                <mui.ListItem
                                                    key={`${version.version}-${index}`}
                                                    divider={index < versionHistory.length - 1}
                                                    sx={{ 
                                                        flexDirection: 'column', 
                                                        alignItems: 'flex-start',
                                                        py: 2, 
                                                        px: 3,
                                                        bgcolor: index === 0 ? alpha('#f0f7ff', 0.3) : 'transparent'
                                                    }}
                                                >
                                                    <mui.Box sx={{ 
                                                        display: 'flex', 
                                                        width: '100%', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'center',
                                                        mb: 1
                                                    }}>
                                                        <mui.Typography 
                                                            variant="subtitle1" 
                                                            component="div"
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center',
                                                                fontWeight: index === 0 ? 600 : 500
                                                            }}
                                                        >
                                                            <HistoryEduRoundedIcon 
                                                                fontSize="small" 
                                                                sx={{ 
                                                                    mr: 1, 
                                                                    color: index === 0 ? 'primary.main' : 'text.secondary' 
                                                                }} 
                                                            />
                                                            Version {version.version}
                                                            {index === 0 && (
                                                                <mui.Chip 
                                                                    label="Current" 
                                                                    size="small" 
                                                                    color="primary"
                                                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            )}
                                                        </mui.Typography>
                                                        
                                                        <mui.Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ fontStyle: 'italic' }}
                                                        >
                                                            {version.timestamp ? new Date(version.timestamp).toLocaleString() : 'Unknown date'}
                                                        </mui.Typography>
                                                    </mui.Box>
                                                    
                                                    <mui.Box sx={{ width: '100%' }}>
                                                        <mui.Typography variant="body2" color="text.secondary">
                                                            Updated by: {version.userName || 'Unknown user'}
                                                        </mui.Typography>
                                                        
                                                        {version.changeDescription && (
                                                            <mui.Typography 
                                                                variant="body2" 
                                                                sx={{ mt: 1, p: 1, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1 }}
                                                            >
                                                                {version.changeDescription}
                                                            </mui.Typography>
                                                        )}
                                                        
                                                        {version.changedFields && version.changedFields.length > 0 && (
                                                            <mui.Box sx={{ mt: 1 }}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    Changed fields:
                                                                </mui.Typography>
                                                                <mui.Box 
                                                                    component="ul" 
                                                                    sx={{ 
                                                                        mt: 0.5, 
                                                                        pl: 2,
                                                                        mb: 0,
                                                                        '& li': { fontSize: '0.875rem' }
                                                                    }}
                                                                >
                                                                    {version.changedFields.map((field, i) => (
                                                                        <li key={i}>{field}</li>
                                                                    ))}
                                                                </mui.Box>
                                                            </mui.Box>
                                                        )}
                                                    </mui.Box>
                                                </mui.ListItem>
                                            ))}
                                        </mui.List>
                                    )}
                                </>
                            }
                            footer={
                                <mui.Button 
                                    onClick={() => setVersionHistoryOpen(false)} 
                                    variant="contained"
                                    sx={{
                                        mb: 1,
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                        }
                                    }}
                                >
                                    Close
                                </mui.Button>
                            }
                        />

                {/* Snackbar for notifications */}
                <mui.Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    sx={{
                        '& .MuiSnackbarContent-root': {
                            backgroundColor: snackbarSeverity === 'success' ? '#4caf50' : '#f44336',
                            color: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            minWidth: '300px',
                            '& .MuiSnackbarAction-root': {
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            },
                        },
                    }}
                >
                    <mui.Alert 
                        onClose={handleSnackbarClose} 
                        severity={snackbarSeverity}
                        sx={{ 
                            width: '100%',
                            '& .MuiAlert-icon': {
                                color: 'white',
                            },
                        }}
                    >
                        {snackbarMessage}
                    </mui.Alert>
                </mui.Snackbar>
                    </mui.Box>
                </mui.Box>
                
                {/* Enhanced Scroll to top button */}
                <FloatingAction in={showScrollTop}>
                    <mui.Fab 
                        color="primary" 
                        size="medium" 
                        aria-label="scroll to top"
                        onClick={scrollToTop}
                        sx={{ 
                            opacity: 0.9,
                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                            '&:hover': {
                                opacity: 1,
                                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                            }
                        }}
                    >
                        <i className="material-icons">keyboard_arrow_up</i>
                    </mui.Fab>
                </FloatingAction>
                
                {/* Saving indicator */}
                <SavingIndicator in={isSaving || saveComplete}>
                    <mui.Paper 
                        elevation={4} 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '10px 20px', 
                            backgroundColor: saveComplete ? '#f8fffa' : '#f5f9ff',
                            border: `1px solid ${saveComplete ? '#d7f0e0' : '#dae7fb'}`,
                            color: saveComplete ? '#43a047' : '#1976d2',
                            borderRadius: '10px',
                            minWidth: '180px',
                            maxWidth: '280px',
                            boxShadow: saveComplete 
                                ? '0 6px 20px rgba(67, 160, 71, 0.1)' 
                                : '0 6px 20px rgba(25, 118, 210, 0.1)',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {saveComplete ? (
                            <>
                                <CheckCircleRoundedIcon color="success" sx={{ mr: 1.5 }} fontSize="small" />
                                <mui.Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ 
                                        fontSize: '0.875rem', 
                                        color: saveComplete ? 'success.main' : 'primary.main',
                                    }}
                                >
                                    Changes saved
                                </mui.Typography>
                            </>
                        ) : (
                            <>
                                <mui.CircularProgress
                                    size={16} 
                                    thickness={6}
                                    sx={{ 
                                        mr: 1.5, 
                                        color: 'primary.main',
                                    }} 
                                />
                                <mui.Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ 
                                        fontSize: '0.875rem', 
                                        color: 'primary.main'
                                    }}
                                >
                                    Saving changes...
                                </mui.Typography>
                            </>
                        )}
                    </mui.Paper>
                </SavingIndicator>
            </ResponsiveContainer>
        </div>
    );

    // Ensure the process status is updated before navigating away
    useEffect(() => {
        let isMounted = true;
        
        // No initial action on mount
        
        // This will run when the component unmounts or before navigation
        return () => {
            isMounted = false;
            
            // Since this is in cleanup, we can't use await directly
            // Instead we'll initiate the update and let it complete
            if (selectedApps && selectedApps.id) {
                // Create a copy of the needed data for the async operation
                const appsCopy = {...selectedApps};
                const provCopy = {...provisioningData};
                const termCopy = {...terminationData};
                const uarCopy = {...uarData};
                const adminCopy = {...adminData};
                
                // Check if all required data is available
                const provValid = 
                    isNonEmptyString(provCopy?.FORM) &&
                    isNonEmptyString(provCopy?.APPROVERS) &&
                    isNonEmptyString(provCopy?.GRANTOR) &&
                    isNonEmptyString(provCopy?.PROCESS_DIFFERENCE);
                
                const termValid = 
                    isNonEmptyString(termCopy?.TERM_NOTIFYER) &&
                    isNonEmptyString(termCopy?.NETWORK_RELIANCE) &&
                    isNonEmptyString(termCopy?.TIMELINESS) &&
                    isNonEmptyString(termCopy?.TERM_DOCUMENTATION) &&
                    isNonEmptyString(termCopy?.TERM_PROCESS) &&
                    isNonEmptyString(termCopy?.TERM_AUTOMATED_DESC) &&
                    isNonEmptyString(termCopy?.DISABLE_TYPE);
                
                const uarValid = 
                    isNonEmptyString(uarCopy?.FREQUENCY) &&
                    isNonEmptyString(uarCopy?.DAYS_TO_COMPLETE) &&
                    isNonEmptyString(uarCopy?.SCOPE_USERS) &&
                    isNonEmptyString(uarCopy?.SCOPE_ROLES) &&
                    isNonEmptyString(uarCopy?.EXTRACTOR) &&
                    isNonEmptyString(uarCopy?.IS_TOOL) &&
                    isNonEmptyString(uarCopy?.EXTRACTION_SEND) &&
                    isNonEmptyString(uarCopy?.REVIEWER) &&
                    isNonEmptyString(uarCopy?.CNA_REVIEWER) &&
                    isNonEmptyString(uarCopy?.CNA_PROCESS) &&
                    isNonEmptyString(uarCopy?.CHANGE_DOCUMENTATION) &&
                    isNonEmptyString(uarCopy?.CHANGE_DOCUMENTATION_SEND) &&
                    isNonEmptyString(uarCopy?.CHANGE_TIMELINE) &&
                    isNonEmptyString(uarCopy?.CHANGE_DOCUMENTATION_REVIEWED) &&
                    isNonEmptyString(uarCopy?.SOD_CHECK);
                
                const adminValid = 
                    isNonEmptyString(adminCopy?.ADMIN_ROLES) &&
                    isNonEmptyString(adminCopy?.CAPABILITIES) &&
                    isNonEmptyString(adminCopy?.ADMIN_REVIEW_PERFORMED) &&
                    isNonEmptyString(adminCopy?.ADMIN_REVIEW_FREQUENCY) &&
                    isNonEmptyString(adminCopy?.ADMIN_REVIEW_DOCUMENT);
                
                const isProcessValid = provValid && termValid && uarValid && adminValid;
                
                // Only update if there's a change
                if (isProcessValid !== (appsCopy?.SETUP_PROCESS === true)) {
                    const updatedData = {
                        ...appsCopy,
                        SETUP_PROCESS: isProcessValid
                    };
                    
                    console.log(`Unmount: Updating SETUP_PROCESS to ${isProcessValid}`);
                    appService.updateApp(appsCopy.id, updatedData)
                        .then(response => {
                            console.log('App status updated during unmount:', response);
                        })
                        .catch(error => {
                            console.error('Error updating app status during unmount:', error);
                        });
                }
            }
        };
    }, [selectedApps, provisioningData, terminationData, uarData, adminData]);

    // Add effective protection against any kind of navigation
    useEffect(() => {
        // This function synchronously checks and updates the process status
        const syncUpdateProcessStatus = () => {
            // Skip if we don't have the necessary data
            if (!selectedApps || !selectedApps.id) return;
            
            // Build accurate status check based on current state
            const provisioningValid = 
                isNonEmptyString(provisioningData?.FORM) &&
                isNonEmptyString(provisioningData?.APPROVERS) &&
                isNonEmptyString(provisioningData?.GRANTOR) &&
                isNonEmptyString(provisioningData?.PROCESS_DIFFERENCE);
            
            const terminationValid = 
                isNonEmptyString(terminationData?.TERM_NOTIFYER) &&
                isNonEmptyString(terminationData?.NETWORK_RELIANCE) &&
                isNonEmptyString(terminationData?.TIMELINESS) &&
                isNonEmptyString(terminationData?.TERM_DOCUMENTATION) &&
                isNonEmptyString(terminationData?.TERM_PROCESS) &&
                isNonEmptyString(terminationData?.TERM_AUTOMATED_DESC) &&
                isNonEmptyString(terminationData?.DISABLE_TYPE);
            
            const uarValid = 
                isNonEmptyString(uarData?.FREQUENCY) &&
                isNonEmptyString(uarData?.DAYS_TO_COMPLETE) &&
                isNonEmptyString(uarData?.SCOPE_USERS) &&
                isNonEmptyString(uarData?.SCOPE_ROLES) &&
                isNonEmptyString(uarData?.EXTRACTOR) &&
                isNonEmptyString(uarData?.IS_TOOL) &&
                isNonEmptyString(uarData?.EXTRACTION_SEND) &&
                isNonEmptyString(uarData?.REVIEWER) &&
                isNonEmptyString(uarData?.CNA_REVIEWER) &&
                isNonEmptyString(uarData?.CNA_PROCESS) &&
                isNonEmptyString(uarData?.CHANGE_DOCUMENTATION) &&
                isNonEmptyString(uarData?.CHANGE_DOCUMENTATION_SEND) &&
                isNonEmptyString(uarData?.CHANGE_TIMELINE) &&
                isNonEmptyString(uarData?.CHANGE_DOCUMENTATION_REVIEWED) &&
                isNonEmptyString(uarData?.SOD_CHECK);
            
            const adminValid = 
                isNonEmptyString(adminData?.ADMIN_ROLES) &&
                isNonEmptyString(adminData?.CAPABILITIES) &&
                isNonEmptyString(adminData?.ADMIN_REVIEW_PERFORMED) &&
                isNonEmptyString(adminData?.ADMIN_REVIEW_FREQUENCY) &&
                isNonEmptyString(adminData?.ADMIN_REVIEW_DOCUMENT);
            
            const isProcessValid = provisioningValid && terminationValid && uarValid && adminValid;
            
            // Only update if there's a change
            if (isProcessValid !== (selectedApps?.SETUP_PROCESS === true)) {
                const updatedData = {
                    ...selectedApps,
                    SETUP_PROCESS: isProcessValid
                };
                
                console.log(`Updating SETUP_PROCESS to ${isProcessValid} synchronously`);
                // Force a synchronous update for navigation events
                appService.updateApp(selectedApps.id, updatedData)
                    .then(response => {
                        console.log('Process status updated successfully before navigation');
                    })
                    .catch(error => {
                        console.error('Error updating process status before navigation:', error);
                    });
            }
        };
        
        // Listen for page navigation events
        const handleBeforeUnload = () => {
            console.log("Page navigation detected, forcing status update");
            syncUpdateProcessStatus();
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // Also capture clicks on any breadcrumb links by delegating to the document
        const handleDocumentClick = (e) => {
            // Check if the click is on a breadcrumb link or any link to applications
            if (e.target.closest('a[href="/applications"]') || 
                e.target.closest('a[href="/dashboard"]') || 
                e.target.textContent === 'My Applications' ||
                e.target.textContent === 'My Dashboard') {
                
                console.log("Breadcrumb or application link clicked, forcing status update");
                syncUpdateProcessStatus();
            }
        };
        
        document.addEventListener('click', handleDocumentClick);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [selectedApps, provisioningData, terminationData, uarData, adminData]);

    // Add scroll position tracking at the top of the component

    // Add a scroll listener effect 
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
            if (scrollPosition > 300) { // Show button after scrolling down 300px
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };
        
        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);
        
        // Clean up
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />
        </div>
    );
}

export default MyApplicationControls;